import { getAccessToken, getRefreshToken, clearSession, setRefreshToken } from "../services/tokenManager";
import { useAuthStore } from '@/stores/authStore';
import type { Quote, CreditNote, TenantSubscription, SubscriptionEvent, UsageCounter } from "../types/models";
import type { LateFeeDetailsData } from "../features/billing/types/billing.types";
import { logger } from "./logger";
// Mappers supprimés: l'API backend et le frontend sont alignés en camelCase

export const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";

/**
 * Safe JSON parsing - prevents crashes from malformed responses
 * @param response - Fetch Response object
 * @returns Parsed JSON or error object
 */
export async function safeJsonParse<T = any>(response: Response): Promise<T> {
  try {
    const text = await response.text();

    if (!text || text.trim() === '') {
      return {} as T;
    }

    return JSON.parse(text) as T;
  } catch (parseError) {
    logger.error('[safeJsonParse] Failed to parse JSON:', {
      status: response.status,
      url: response.url,
    });

    // Return a structured error object instead of crashing
    return {
      error: 'invalid_response',
      message: 'Réponse invalide du serveur',
    } as T;
  }
}

/**
 * Safe fetch with timeout and network error handling
 */
export async function safeFetch(url: string, options?: RequestInit & { timeout?: number }): Promise<Response> {
  const { timeout = 30000, ...fetchOptions } = options || {};

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('La requête a expiré (timeout)');
      }
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
      }
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetch authentifié avec retry automatique sur 401.
 * Si le premier appel retourne 401 et qu'un cookie HttpOnly existe,
 * tente un refresh puis rejoue la requête avec le nouveau token.
 */
let _refreshPromise: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  // Dédup : si un refresh est déjà en cours, réutiliser la même promesse
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/core/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 409) {
          clearSession();
        }
        return false;
      }
      const data = await response.json();
      if (data.success && data.data?.accessToken) {
        const { setAccessToken } = await import('../services/tokenManager');
        setAccessToken(data.data.accessToken, data.data.expiresIn || 900);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      _refreshPromise = null;
    }
  })();

  return _refreshPromise;
}

export async function fetchWithAuth(
  url: string,
  options?: RequestInit & { timeout?: number }
): Promise<Response> {
  // Injecter le token dans les headers
  const buildHeaders = (): HeadersInit => {
    const token = getAccessToken();
    const existing = (options?.headers as Record<string, string>) || {};
    const isFormData = options?.body instanceof FormData;
    return {
      // Ne pas définir Content-Type pour FormData — le navigateur le gère avec le boundary
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token && { Authorization: `Bearer ${token}` }),
      // Les headers explicites (ex: portalToken) priment sur le token auto
      ...existing,
    };
  };

  // Premier essai
  const response = await safeFetch(url, { ...options, headers: buildHeaders() });

  // Si 401, tenter un refresh puis rejouer
  if (response.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      return safeFetch(url, { ...options, headers: buildHeaders() });
    }
  }

  return response;
}

// Helper pour construire les URLs des fichiers statiques (logos, etc.)
export const getAssetUrl = (path: string) => {
  if (!path) return "/logo.svg"; // Logo par défaut
  if (path.startsWith("http")) return path;
  if (path.startsWith("/uploads")) return `${API_URL}${path}`;
  return path;
};

// Helper pour les requêtes authentifiées
export const getAuthHeaders = () => {
  const token = getAccessToken();
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

/**
 * Parse les erreurs depuis le backend
 * Format unifié backend: { success: false, error: { message, code, status, details } }
 * @returns Message d'erreur compréhensible pour l'utilisateur
 */
const parseApiError = (errorData: any): string => {
  // Format unifié: errorData.error est un objet { message, code, status, details }
  if (errorData.error && typeof errorData.error === 'object') {
    const err = errorData.error;

    // Si details Zod présents, formater en liste des messages
    if (err.details && Array.isArray(err.details) && err.details.length > 0) {
      const messages = err.details
        .map((d: any) => d.message)
        .filter(Boolean)
        .join(', ');
      return messages || err.message || "Une erreur est survenue";
    }

    // Sinon retourner le message principal
    return err.message || "Une erreur est survenue";
  }

  // Fallback pour ancien format ou erreurs non standardisées
  return errorData.message || errorData.error || "Une erreur est survenue";
};

// Authentification
export const authAPI = {
  /**
   * Authentification de l'utilisateur
   * ✅ Retourne { user, accessToken, refreshToken } avec structure TenantUser
   */
  login: async (email: string, password: string) => {
    const response = await safeFetch(`${API_URL}/core/auth/login`, {
      method: "POST",
      credentials: "include", // ✅ Important: envoie et reçoit les cookies HttpOnly (refresh token)
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      const message = parseApiError(error);
      throw new Error(message);
    }

    const json = await safeJsonParse(response);

    // Le backend retourne { success: true, data: { user, accessToken, refreshToken } }
    // On retourne directement data qui contient { user: { id, email, tenantId, companyId, role, fullName }, accessToken, refreshToken }
    return json.data || json;
  },

  register: async (
    email: string,
    password: string,
    fullName: string,
    tenantId?: string
  ) => {
    // Générer un tenantId UNIQUE pour chaque utilisateur si non fourni
    // Format: tenant-{timestamp}-{random} pour garantir l'unicité
    const finalTenantId = tenantId || `tenant-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    const response = await safeFetch(`${API_URL}/core/auth/register`, {
      method: "POST",
      credentials: "include", // ✅ Important: envoie et reçoit les cookies HttpOnly (refresh token)
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        fullName,
        tenantId: finalTenantId
      }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      const message = parseApiError(error);
      throw new Error(message);
    }

    const json = await safeJsonParse(response);

    // Le backend retourne { success: true, message: "...", data: { user, accessToken, refreshToken } }
    // On retourne directement data
    return json.data || json;
  },

  /**
   * Récupère les données de l'utilisateur connecté
   * ✅ Retourne la structure TenantUser (tenantId, companyId, role)
   */
  me: async () => {
    const response = await fetchWithAuth(`${API_URL}/core/auth/me`);

    if (!response.ok) {
      throw new Error("Session expirée");
    }

    const json = await safeJsonParse(response);

    // Le backend retourne { success: true, data: { id, email, tenantId, companyId, role, fullName } }
    // On retourne directement data qui contient déjà la bonne structure
    return json.data || json;
  },

  logout: () => {
    clearSession(); // ✅ Utilise le service sessionStorage
  },

  /**
   * Rafraîchit le token d'accès avec le refresh token
   */
  refresh: async (refreshToken: string) => {
    const response = await safeFetch(`${API_URL}/core/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * Changer le mot de passe de l'utilisateur connecté
   */
  changePassword: async (oldPassword: string, newPassword: string) => {
    const response = await safeFetch(`${API_URL}/core/auth/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ oldPassword, newPassword }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return await safeJsonParse(response);
  },
};

// =============================================
// OAuth API - SSO Google/Microsoft
// =============================================
export interface OAuthProvider {
  provider: 'GOOGLE' | 'MICROSOFT' | 'APPLE';
  enabled: boolean;
}

export interface OAuthLinkedAccount {
  provider: 'GOOGLE' | 'MICROSOFT' | 'APPLE';
  email: string;
  linkedAt: string;
}

export const oauthAPI = {
  /**
   * Récupère les providers OAuth disponibles
   */
  getProviders: async (): Promise<OAuthProvider[]> => {
    const response = await safeFetch(`${API_URL}/core/auth/oauth/providers`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des providers OAuth");
    }

    const json = await safeJsonParse(response);
    return json.data?.providers || [];
  },

  /**
   * Génère l'URL d'autorisation OAuth
   * Redirige directement vers le provider
   */
  getAuthUrl: (provider: 'google' | 'microsoft', returnUrl?: string, link?: boolean): string => {
    const params = new URLSearchParams();
    if (returnUrl) params.set('returnUrl', returnUrl);
    if (link) params.set('link', 'true');

    return `${API_URL}/core/auth/oauth/${provider}${params.toString() ? '?' + params.toString() : ''}`;
  },

  /**
   * Récupère les comptes OAuth liés à l'utilisateur connecté
   */
  getLinkedAccounts: async (): Promise<OAuthLinkedAccount[]> => {
    const response = await fetchWithAuth(`${API_URL}/core/auth/oauth/accounts`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des comptes liés");
    }

    const json = await safeJsonParse(response);
    return json.data?.accounts || [];
  },

  /**
   * Délie un compte OAuth
   */
  unlinkAccount: async (provider: 'google' | 'microsoft'): Promise<void> => {
    const response = await fetchWithAuth(`${API_URL}/core/auth/oauth/${provider}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }
  },

  /**
   * Initier la connexion OAuth (redirige vers le provider)
   */
  initiateLogin: (provider: 'google' | 'microsoft') => {
    window.location.href = oauthAPI.getAuthUrl(provider, '/dashboard');
  },

  /**
   * Lier un compte OAuth à l'utilisateur connecté
   */
  initiateLink: (provider: 'google' | 'microsoft') => {
    window.location.href = oauthAPI.getAuthUrl(provider, '/settings/security', true);
  },
};

// =====================
// DEVICES API (Phase 5 Security - Device Fingerprinting)
// ISO 27001 A.9.4.2 - Procédures de connexion sécurisée
// =====================

export interface LoginDevice {
  id: string;
  deviceName: string;
  deviceType: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  browser: string;
  os: string;
  isTrusted: boolean;
  trustExpiresAt: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  lastIpAddress: string | null;
  lastLocation: string | null;
  loginCount: number;
}

export interface DevicesListResponse {
  devices: LoginDevice[];
  currentDeviceId: string | null;
}

export const devicesAPI = {
  /**
   * Récupérer tous les devices de l'utilisateur connecté
   * Inclut l'ID du device actuel pour le highlighting
   */
  listDevices: async (): Promise<DevicesListResponse> => {
    const response = await fetchWithAuth(`${API_URL}/core/auth/devices`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des devices");
    }

    const json = await safeJsonParse(response);
    return {
      devices: json.data?.devices || [],
      currentDeviceId: json.data?.currentDeviceId || null,
    };
  },

  /**
   * Marquer un device comme de confiance
   */
  trustDevice: async (deviceId: string, durationDays?: number): Promise<void> => {
    const response = await fetchWithAuth(`${API_URL}/core/auth/devices/${deviceId}/trust`, {
      method: 'POST',
      body: JSON.stringify({ durationDays }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }
  },

  /**
   * Révoquer la confiance d'un device
   */
  revokeDevice: async (deviceId: string): Promise<void> => {
    const response = await fetchWithAuth(`${API_URL}/core/auth/devices/${deviceId}/revoke`, {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }
  },

  /**
   * Supprimer un device
   */
  deleteDevice: async (deviceId: string): Promise<void> => {
    const response = await fetchWithAuth(`${API_URL}/core/auth/devices/${deviceId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }
  },

  /**
   * Révoquer tous les devices (logout de partout)
   */
  revokeAllDevices: async (): Promise<{ revokedCount: number }> => {
    const response = await fetchWithAuth(`${API_URL}/core/auth/devices/revoke-all`, {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || { revokedCount: 0 };
  },
};

// Gestion des devis
export const quotesAPI = {
  create: async (quoteData: Partial<Quote>) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes`, {
      method: "POST",
      body: JSON.stringify(quoteData),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      // ✅ FIX: Handle different error response formats
      const errorMessage = error?.message || error?.error || (typeof error === 'string' ? error : JSON.stringify(error)) || "Erreur lors de la création du devis";
      throw new Error(errorMessage);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  getAll: async () => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des devis");
    }

    const data = await safeJsonParse(response);
    const quotes = data.data || data.quotes || data;
    return Array.isArray(quotes) ? quotes : [];
  },

  getById: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}`);

    if (!response.ok) {
      throw new Error("Devis non trouvé");
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  update: async (id: string, quoteData: any) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(quoteData),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  delete: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la suppression du devis");
    }

    return safeJsonParse(response);
  },

  updateStatus: async (id: string, status: string) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  duplicate: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}/duplicate`, {
      method: "POST",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  send: async (id: string, email: string, subject?: string, message?: string) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}/send`, {
      method: "POST",
      body: JSON.stringify({ email, subject, message }),
    });

    if (!response.ok) {
      const errorResponse = await safeJsonParse(response);
      const errorMessage = errorResponse.error?.message || errorResponse.message || "Erreur lors de l'envoi du devis";
      throw new Error(errorMessage);
    }

    return safeJsonParse(response);
  },

  downloadPDF: async (id: string): Promise<Blob> => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}/pdf`);

    if (!response.ok) {
      throw new Error("Erreur lors du téléchargement du PDF");
    }

    return response.blob();
  },

  generateCerfaTVA: async (id: string, data: {
    vatRate: number;
    occupantStatus: 'owner' | 'tenant' | 'syndic' | 'other';
    workNature: string;
    siteAddress?: string;
    siteZipCode?: string;
    siteCity?: string;
  }): Promise<Blob> => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}/cerfa-tva-reduite`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorResponse = await safeJsonParse(response);
      const errorMessage = errorResponse.error?.message || errorResponse.message || 'Erreur lors de la génération du Cerfa';
      throw new Error(errorMessage);
    }

    return response.blob();
  },

  convertToInvoice: async (id: string, options?: { invoiceType?: string; issueDate?: string }) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}/convert`, {
      method: "POST",
      body: JSON.stringify(options || {}),
    });

    if (!response.ok) {
      const errorResponse = await safeJsonParse(response);
      const errorMessage = errorResponse.error?.message || errorResponse.message || "Erreur lors de la conversion en facture";
      throw new Error(errorMessage);
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  createDepositInvoice: async (id: string, data: { depositType: 'PERCENT' | 'FIXED'; depositValue: number; issueDate: string; dueDate: string }) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}/deposit-invoice`, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorResponse = await safeJsonParse(response);
      const errorMessage = errorResponse.error?.message || errorResponse.message || "Erreur lors de la création de la facture d'acompte";
      throw new Error(errorMessage);
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  createFinalInvoice: async (id: string, data: { issueDate: string; dueDate: string }) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}/final-invoice`, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorResponse = await safeJsonParse(response);
      const errorMessage = errorResponse.error?.message || errorResponse.message || "Erreur lors de la création de la facture de solde";
      throw new Error(errorMessage);
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  getDepositSummary: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}/deposit-summary`);

    if (!response.ok) {
      const errorResponse = await safeJsonParse(response);
      const errorMessage = errorResponse.error?.message || errorResponse.message || "Erreur lors de la récupération du résumé des acomptes";
      throw new Error(errorMessage);
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  sign: async (id: string, signatureData: { signatureImage?: string; signerName?: string; signerEmail?: string }) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${id}/sign`, {
      method: "POST",
      body: JSON.stringify(signatureData),
    });

    if (!response.ok) {
      const errorResponse = await safeJsonParse(response);
      const errorMessage = errorResponse.error?.message || errorResponse.message || "Erreur lors de la signature du devis";
      throw new Error(errorMessage);
    }

    return safeJsonParse(response);
  },

  getStats: async () => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/stats`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des statistiques");
    }

    const data = await safeJsonParse(response);
    return data.data || data;
  },

  getExpired: async () => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/expired`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des devis expirés");
    }

    const data = await safeJsonParse(response);
    return data.data || data;
  },

  accept: async (id: string) => {
    return quotesAPI.updateStatus(id, 'ACCEPTED');
  },

  reject: async (id: string) => {
    return quotesAPI.updateStatus(id, 'REJECTED');
  },
};

// Gestion des factures
export const invoicesAPI = {
  create: async (invoiceData: any) => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices`, {
      method: "POST",
      body: JSON.stringify(invoiceData),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  createFromQuote: async (quoteId: string) => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/from-quote/${quoteId}`, {
      method: "POST",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  getAll: async () => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des factures");
    }

    const data = await safeJsonParse(response);
    return data.data || data.invoices || data;
  },

  getById: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/${id}`);


    if (!response.ok) {
      throw new Error("Facture non trouvée");
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  update: async (id: string, invoiceData: any) => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/${id}`, {
      method: "PATCH",
      body: JSON.stringify(invoiceData),
    });

    if (!response.ok) {
      const errorResponse = await safeJsonParse(response);
      // Backend returns { success: false, error: { message: "...", code: "..." } }
      const errorMessage = errorResponse.error?.message || errorResponse.message || "Erreur lors de la mise à jour de la facture";
      throw new Error(errorMessage);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  markAsPaid: async (id: string, _paidAmount?: number) => {
    // Utilise la route /status existante avec le statut PAID
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status: "PAID" }),
    });

    if (!response.ok) {
      const errorResponse = await safeJsonParse(response);
      // Backend returns { success: false, error: { message: "...", code: "..." } }
      const errorMessage = errorResponse.error?.message || errorResponse.message || "Erreur lors de la mise à jour de la facture";
      throw new Error(errorMessage);
    }

    return safeJsonParse(response);
  },

  delete: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/${id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const errorResponse = await safeJsonParse(response);
      const errorMessage = errorResponse.error?.message || errorResponse.message || "Erreur lors de la suppression de la facture";
      throw new Error(errorMessage);
    }

    return safeJsonParse(response);
  },

  updateStatus: async (id: string, status: string) => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const errorResponse = await safeJsonParse(response);
      // Backend returns { success: false, error: { message: "...", code: "..." } }
      const errorMessage = errorResponse.error?.message || errorResponse.message || "Erreur lors du changement de statut";
      throw new Error(errorMessage);
    }

    return safeJsonParse(response);
  },

  send: async (id: string, email: string, subject?: string, message?: string) => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/${id}/send`, {
      method: "POST",
      body: JSON.stringify({ email, subject, message }),
    });

    if (!response.ok) {
      const errorResponse = await safeJsonParse(response);
      const errorMessage = errorResponse.error?.message || errorResponse.message || "Erreur lors de l'envoi de la facture";
      throw new Error(errorMessage);
    }

    return safeJsonParse(response);
  },

  downloadPDF: async (id: string): Promise<Blob> => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/${id}/pdf`);


    if (!response.ok) {
      throw new Error("Erreur lors du téléchargement du PDF");
    }

    return response.blob();
  },

  downloadFacturXPDF: async (id: string): Promise<Blob> => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/${id}/facturx`);


    if (!response.ok) {
      throw new Error("Erreur lors du téléchargement du PDF Factur-X");
    }

    return response.blob();
  },

  getStats: async () => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/stats`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des statistiques");
    }

    const data = await safeJsonParse(response);
    return data.data || data;
  },

  getOverdue: async () => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/overdue`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des factures en retard");
    }

    const data = await safeJsonParse(response);
    return data.data || data;
  },

  // S8 — Pénalités de retard
  getLateFeeDetails: async (id: string): Promise<LateFeeDetailsData> => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/${id}/late-fees`);

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  calculateLateFees: async (id: string): Promise<LateFeeDetailsData> => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices/${id}/late-fees/calculate`, {
      method: "POST",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },
};

// ============================================
// 📧 EMAIL TEMPLATES API
// ============================================
export const emailTemplatesAPI = {
  getAll: async (code?: string) => {
    const url = code
      ? `${API_URL}/templates/emails?code=${code}`
      : `${API_URL}/templates/emails`;

    const response = await fetchWithAuth(url);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des templates");
    }

    return safeJsonParse(response);
  },

  getById: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/templates/emails/${id}`);

    if (!response.ok) {
      throw new Error("Template non trouvé");
    }

    return safeJsonParse(response);
  },

  create: async (data: any) => {
    const response = await fetchWithAuth(`${API_URL}/templates/emails`, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  update: async (id: string, data: any) => {
    const response = await fetchWithAuth(`${API_URL}/templates/emails/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  delete: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/templates/emails/${id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la suppression du template");
    }

    return safeJsonParse(response);
  },

  getTypes: async () => {
    const response = await fetchWithAuth(`${API_URL}/templates/emails/types`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des types de templates");
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 📧 EMAIL TEMPLATE TYPES (SKILL FACTURXPRO)
// ============================================
// ✅ Interfaces strictes TypeScript (pas de `any`)

interface EmailTemplateContent {
  subject: string;
  body: string;
}

interface UpdateReminderSettingsInput {
  enabled: boolean;
  firstReminderDays: number;
  secondReminderDays: number;
  thirdReminderDays: number;
  firstReminderTemplate: EmailTemplateContent;
  secondReminderTemplate: EmailTemplateContent;
  thirdReminderTemplate: EmailTemplateContent;
  sendCopyToCompany: boolean;
  autoMarkOverdue: boolean;
}

interface UpdateQuoteReminderSettingsInput {
  enabled: boolean;
  firstReminderDays: number;
  secondReminderDays: number;
  thirdReminderDays: number;
  firstReminderTemplate: EmailTemplateContent;
  secondReminderTemplate: EmailTemplateContent;
  thirdReminderTemplate: EmailTemplateContent;
  sendCopyToCompany: boolean;
  stopOnExpiry: boolean;
}

// ============================================
// ⏰ REMINDERS API
// ============================================
export const remindersAPI = {
  getSettings: async () => {
    const response = await fetchWithAuth(`${API_URL}/reminders/settings`);


    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("NOT_FOUND");
      }
      throw new Error("Erreur lors de la récupération des paramètres");
    }

    return safeJsonParse(response);
  },

  // ✅ SKILL FACTURXPRO: Type strict (pas `any`)
  updateSettings: async (data: UpdateReminderSettingsInput) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/settings`, {
      method: "PUT",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  // ✅ Nouvelles fonctions pour PaymentReminders
  getInvoices: async () => {
    const response = await fetchWithAuth(`${API_URL}/reminders/invoices`);

    if (!response.ok)
      throw new Error("Erreur lors de la récupération des factures");
    return safeJsonParse(response);
  },

  getStatistics: async () => {
    const response = await fetchWithAuth(`${API_URL}/reminders/statistics`);

    if (!response.ok)
      throw new Error("Erreur lors de la récupération des statistiques");
    return safeJsonParse(response);
  },

  getHistory: async (invoiceId: number) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/history/${invoiceId}`);

    if (!response.ok)
      throw new Error("Erreur lors de la récupération de l'historique");
    return safeJsonParse(response);
  },

  processReminders: async () => {
    const response = await fetchWithAuth(`${API_URL}/reminders/process`, {
      method: "POST",
    });
    if (!response.ok) throw new Error("Erreur lors du traitement des relances");
    return safeJsonParse(response);
  },

  sendManualInvoice: async (invoiceId: number) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/manual/${invoiceId}`, {
      method: "POST",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  // ============================================
  // 🆕 POLICIES API (Backend-compatible)
  // ============================================

  getPolicies: async () => {
    const response = await fetchWithAuth(`${API_URL}/reminders/policies`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des policies");
    }

    const json = await safeJsonParse(response);
    return json.data || json; // Support both formats
  },

  getPolicyById: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/policies/${id}`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération de la policy");
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  createPolicy: async (data: any) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/policies`, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  updatePolicy: async (id: string, data: any) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/policies/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  deletePolicy: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/policies/${id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  togglePolicy: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/policies/${id}/toggle`, {
      method: "PATCH",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },
};

// ============================================
// 📋 QUOTE REMINDERS API
// ============================================
export const quoteRemindersAPI = {
  getSettings: async () => {
    const response = await fetchWithAuth(`${API_URL}/reminders/quote/settings`);


    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("NOT_FOUND");
      }
      throw new Error(
        "Erreur lors de la récupération des paramètres de relances devis"
      );
    }

    return safeJsonParse(response);
  },

  // ✅ SKILL FACTURXPRO: Type strict (pas `any`)
  updateSettings: async (data: UpdateQuoteReminderSettingsInput) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/quote/settings`, {
      method: "PUT",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  // ✅ Nouvelles fonctions pour PaymentReminders
  getQuotes: async () => {
    const response = await fetchWithAuth(`${API_URL}/reminders/quote/quotes`);

    if (!response.ok)
      throw new Error("Erreur lors de la récupération des devis à relancer");
    return safeJsonParse(response);
  },

  getStatistics: async () => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/stats`);

    if (!response.ok)
      throw new Error("Erreur lors de la récupération des statistiques");
    return safeJsonParse(response);
  },

  getHistory: async (quoteId: number) => {
    const response = await fetchWithAuth(
      `${API_URL}/billing/quotes/${quoteId}/history`,
      {
      }
    );
    if (!response.ok)
      throw new Error("Erreur lors de la récupération de l'historique");
    return safeJsonParse(response);
  },

  processReminders: async () => {
    const response = await fetchWithAuth(`${API_URL}/reminders/quote/process`, {
      method: "POST",
    });
    if (!response.ok) throw new Error("Erreur lors du traitement des relances");
    return safeJsonParse(response);
  },

  sendManual: async (quoteId: number) => {
    const response = await fetchWithAuth(
      `${API_URL}/reminders/quote/manual/${quoteId}`,
      {
        method: "POST",
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  // ============================================
  // 🆕 POLICIES API (Backend-compatible)
  // ============================================
  getPolicies: async () => {
    const response = await fetchWithAuth(`${API_URL}/reminders/policies`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des policies");
    }
    const json = await safeJsonParse(response);
    return json.data || json;
  },

  getPolicyById: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/policies/${id}`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération de la policy");
    }
    const json = await safeJsonParse(response);
    return json.data || json;
  },

  createPolicy: async (data: any) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/policies`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error("Erreur lors de la création de la policy");
    }
    const json = await safeJsonParse(response);
    return json.data || json;
  },

  updatePolicy: async (id: string, data: any) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/policies/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error("Erreur lors de la mise à jour de la policy");
    }
    const json = await safeJsonParse(response);
    return json.data || json;
  },

  deletePolicy: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/policies/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      throw new Error("Erreur lors de la suppression de la policy");
    }
    const json = await safeJsonParse(response);
    return json.data || json;
  },

  togglePolicy: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/reminders/policies/${id}/toggle`, {
      method: "PATCH",
    });
    if (!response.ok) {
      throw new Error("Erreur lors de la modification du statut");
    }
    const json = await safeJsonParse(response);
    return json.data || json;
  },
};

// ============================================
// 🎨 LOGO UPLOAD API - ❌ DEPRECATED
// ============================================
// ⚠️ Cette implémentation est obsolète et ne doit plus être utilisée.
// Elle envoyait les images en base64 dans les headers, causant l'erreur 431.
//
// ✅ Utilisez companiesAPI.uploadLogo() à la place qui envoie FormData correctement.

// ============================================
// 🚀 ONBOARDING API
// ============================================
export const onboardingAPI = {
  update: async (payload: any) => {
    const { step, data } = payload;

    // Step 1: Company Info → POST /companies
    if (step === 1 && data.companyName) {
      const companyPayload = {
        legalName: data.companyName, // Mapping: companyName → legalName
        tradeName: data.companyName, // tradeName = legalName par défaut
        registrationNumber: data.siret?.replace(/\s/g, ''), // SIRET sans espaces
        vatNumber: data.tvaNumber?.replace(/\s/g, ''), // TVA sans espaces
        taxCountryCode: 'FR',
        addressLine1: data.address,
        addressLine2: null,
        zipCode: data.postalCode,
        city: data.city,
        countryCode: 'FR',
        email: data.email,
        phone: data.phone,
        defaultCurrencyCode: 'EUR',
        apeCode: data.apeCode || undefined,
        businessType: data.businessType || 'SERVICE',
        companyTrade: data.companyTrade || undefined,
      };

      const response = await fetchWithAuth(`${API_URL}/core/companies`, {
        method: "POST",
        body: JSON.stringify(companyPayload),
      });

      if (!response.ok) {
        const errorData = await safeJsonParse(response).catch(() => ({}));
        throw new Error(parseApiError(errorData));
      }

      const result = await safeJsonParse(response);

      // ✅ Recharger le user depuis le backend pour avoir le companyId à jour
      if (result.data?.id) {
        try {
          const userResponse = await fetchWithAuth(`${API_URL}/core/auth/me`);


          if (userResponse.ok) {
            const userData = await userResponse.json();
            useAuthStore.getState().setUser(userData.data);
            logger.log('✅ User rechargé avec companyId:', userData.data?.companyId);
          }
        } catch (error) {
          logger.error('❌ Erreur rechargement user:', error);
          // Fallback: mise à jour manuelle
          const currentUser = useAuthStore.getState().user;
          if (currentUser) {
            useAuthStore.getState().setUser({ ...currentUser, companyId: result.data.id });
          }
        }
      }

      return result;
    }

    // Step 2: VAT & Legal → PUT /companies/:id
    if (step === 2) {
      const user = useAuthStore.getState().user;
      const companyId = user?.companyId;

      if (!companyId) {
        throw new Error('Company ID manquant. Veuillez compléter l\'étape 1 d\'abord.');
      }

      const settingsPayload = {
        legalNotesDefault: data.legalMention || null,
        // Note: vatRegime et defaultVatRate ne sont pas dans le modèle Company
        // Ils pourraient être stockés dans CompanySettings ou une autre table
      };

      const response = await fetchWithAuth(`${API_URL}/core/companies/${companyId}`, {
        method: "PUT",
        body: JSON.stringify(settingsPayload),
      });

      if (!response.ok) {
        const errorData = await safeJsonParse(response).catch(() => ({}));
        throw new Error(parseApiError(errorData));
      }

      return safeJsonParse(response);
    }

    // Step 3: Logo & Branding → POST /companies/:id/logo + Preferences
    if (step === 3) {
      const user = useAuthStore.getState().user;
      const companyId = user?.companyId;

      if (!companyId) {
        throw new Error('Company ID manquant. Veuillez compléter l\'étape 1 d\'abord.');
      }

      // ✅ Logo déjà uploadé par companiesAPI.uploadLogo() dans Step3LogoBranding
      // Pas besoin de le re-poster ici, il est déjà sauvegardé dans la Company

      // Sauvegarder la couleur primaire dans CompanyTemplatePreference
      if (data.primaryColor) {
        const colorResponse = await fetchWithAuth(`${API_URL}/templates/preferences/${companyId}/colors`, {
          method: "PATCH",
          body: JSON.stringify({
            colorPrimary: data.primaryColor,
            colorSecondary: data.secondaryColor || null, // Optional
          }),
        });

        if (!colorResponse.ok) {
          const errorData = await colorResponse.json().catch(() => ({}));
          throw new Error(parseApiError(errorData));
        }
      }

      return { success: true, message: 'Logo et branding sauvegardés' };
    }

    // Step 4: Clients & Products → POST /catalog/clients + POST /catalog/products
    if (step === 4) {
      const user = useAuthStore.getState().user;
      const companyId = user?.companyId;

      if (!companyId) {
        throw new Error('Company ID manquant. Veuillez compléter l\'étape 1 d\'abord.');
      }

      const createdClients: any[] = [];
      const createdProducts: any[] = [];

      // Créer les clients
      if (data.clients && Array.isArray(data.clients) && data.clients.length > 0) {
        for (const client of data.clients) {
          // ✅ Ignorer les clients sans nom
          const clientName = client.fullName || client.companyName || client.name || '';
          if (!clientName || clientName.trim() === '') {
            logger.warn('⚠️ Client sans nom ignoré:', client);
            continue;
          }

          try {
            const clientPayload = {
              displayName: clientName,
              email: client.email || null,
              phone: client.phone || null,
              contactName: clientName, // Utiliser le nom comme contactName par défaut
            };

            const response = await fetchWithAuth(`${API_URL}/catalog/clients`, {
              method: 'POST',
              body: JSON.stringify(clientPayload),
            });

            if (response.ok) {
              const result = await safeJsonParse(response);
              createdClients.push(result.data);
              logger.log('✅ Client créé:', result.data);
            } else {
              const errorData = await safeJsonParse(response).catch(() => ({}));
              logger.error('❌ Erreur création client:', errorData);
            }
          } catch (error) {
            logger.error('❌ Erreur création client:', error);
          }
        }
      }

      // Créer les produits
      if (data.products && Array.isArray(data.products) && data.products.length > 0) {
        // D'abord récupérer les TaxRates pour mapper vatRate → taxRateId
        const taxRatesResponse = await fetchWithAuth(`${API_URL}/catalog/tax-rates`);


        let taxRatesMap: Record<number, string> = {};
        if (taxRatesResponse.ok) {
          const taxRatesData = await taxRatesResponse.json();
          const taxRates = taxRatesData.data || [];
          // Créer un mapping: rate → id
          taxRatesMap = taxRates.reduce((acc: Record<number, string>, tr: any) => {
            acc[tr.rate] = tr.id;
            return acc;
          }, {});
        }

        for (const product of data.products) {
          try {
            // Utiliser le defaultTaxRateId si fourni, sinon mapper depuis vatRate
            const defaultTaxRateId = product.defaultTaxRateId ||
                                      taxRatesMap[product.vatRate] ||
                                      taxRatesMap[20]; // Fallback 20%

            const productPayload = {
              name: product.name || product.description?.substring(0, 50) || 'Produit',
              description: product.description || null,
              unitPrice: product.unitPrice,
              taxRateId: defaultTaxRateId,
              categoryId: product.categoryId || null,
              unit: product.unit || null,
              isActive: true,
            };

            const response = await fetchWithAuth(`${API_URL}/catalog/products`, {
              method: 'POST',
              body: JSON.stringify(productPayload),
            });

            if (response.ok) {
              const result = await safeJsonParse(response);
              createdProducts.push(result.data);
              logger.log('✅ Produit créé:', result.data);
            } else {
              const errorData = await safeJsonParse(response).catch(() => ({}));
              logger.error('❌ Erreur création produit:', errorData);
            }
          } catch (error) {
            logger.error('❌ Erreur création produit:', error);
          }
        }
      }

      return {
        success: true,
        message: `${createdClients.length} client(s) et ${createdProducts.length} produit(s) créés`,
        data: {
          clients: createdClients,
          products: createdProducts,
        },
      };
    }

    // Steps 5-6: À implémenter (Devis/Factures)
    return { success: true, message: `Step ${step} saved (local only)` };
  },

  complete: async () => {
    // Marquer l'onboarding comme terminé
    return { success: true, message: "Onboarding terminé avec succès" };
  },
};

// ============================================
// 📈 SESSION METRICS API
// ============================================
export const sessionMetricsAPI = {
  getOverview: async () => {
    const response = await fetchWithAuth(
      `${API_URL}/superadmin/session-metrics/overview`,
      {
      }
    );

    if (!response.ok) {
      throw new Error(
        "Erreur lors de la récupération des métriques de session"
      );
    }

    return safeJsonParse(response);
  },

  getSuspicious: async () => {
    const response = await fetchWithAuth(
      `${API_URL}/superadmin/session-metrics/suspicious`,
      {
      }
    );

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des sessions suspectes");
    }

    return safeJsonParse(response);
  },

  revokeSuspicious: async () => {
    const response = await fetchWithAuth(
      `${API_URL}/superadmin/session-metrics/revoke-suspicious`,
      {
        method: "POST",
      }
    );

    if (!response.ok) {
      throw new Error("Erreur lors de la révocation des sessions suspectes");
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 👤 CLIENT PORTAL IMPERSONATION API (SuperAdmin)
// ============================================
export const clientPortalImpersonationAPI = {
  /**
   * Liste les utilisateurs du portail client (pour le picker d'impersonation)
   */
  getUsers: async (search?: string) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    params.set('limit', '20');

    const response = await fetchWithAuth(
      `${API_URL}/superadmin/client-portal-users?${params}`,
      {
      }
    );

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des utilisateurs');
    }

    const data = await safeJsonParse(response);
    return data.data || [];
  },

  /**
   * Impersonifier un utilisateur du portail client
   * Retourne un token de session + URL de redirection
   */
  impersonate: async (clientPortalUserId: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/superadmin/impersonate/client-portal/${clientPortalUserId}`,
      {
        method: 'POST',
      }
    );

    return safeJsonParse(response);
  },
};

// ============================================
// 🔍 OCR INVOICE EXTRACTION API
// ============================================
export const ocrInvoiceExtractionAPI = {
  /**
   * Extrait les données d'une facture via OCR/ML
   * Le provider est configuré côté SuperAdmin - le client n'a qu'à uploader
   * Utilise OpenAI Vision par défaut si configuré, sinon Tesseract
   */
  extract: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    // Pour FormData, ne pas définir Content-Type (le navigateur le fait automatiquement avec boundary)
    const token = getAccessToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await safeFetch(`${API_URL}/ocr/extract`, {
      method: "POST",
      headers,
      body: formData,
    });

    const data = await safeJsonParse(response);

    if (!response.ok) {
      return {
        success: false,
        error: data.error || "Erreur lors de l'extraction OCR",
      };
    }

    return { success: true, data };
  },
};

// ============================================
// 📦 BATCH OCR API (Multi-file Import)
// ============================================
export const batchOCRAPI = {
  /**
   * POST /ocr/batch-extract — OCR batch de N documents
   */
  batchExtract: async (documents: Array<{ documentUrl: string; documentType?: string; filename?: string }>) => {
    // OCR takes ~10s per document — scale timeout accordingly
    const response = await fetchWithAuth(`${API_URL}/ocr/batch-extract`, {
      method: 'POST',
      body: JSON.stringify({ documents }),
      timeout: 10000 * documents.length + 30000,
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * POST /ocr/batch-create-expenses — Créer N dépenses validées
   */
  batchCreateExpenses: async (expenses: unknown[]) => {
    const response = await fetchWithAuth(`${API_URL}/ocr/batch-create-expenses`, {
      method: 'POST',
      body: JSON.stringify({ expenses }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * POST /ocr/batch-import-invoices — Valider N factures pour import
   */
  batchImportInvoices: async (invoices: unknown[]) => {
    const response = await fetchWithAuth(`${API_URL}/ocr/batch-import-invoices`, {
      method: 'POST',
      body: JSON.stringify({ invoices }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * POST /ocr/split-and-extract — Split PDF multi-factures + OCR chaque facture
   */
  splitAndExtract: async (documentUrl: string) => {
    // PDF split + OCR per invoice — can take several minutes
    const response = await fetchWithAuth(`${API_URL}/ocr/split-and-extract`, {
      method: 'POST',
      body: JSON.stringify({ documentUrl }),
      timeout: 180000,
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * POST /ocr/split-and-extract-async — Version async (retourne batchImportId)
   */
  splitAndExtractAsync: async (documentUrl: string) => {
    const response = await fetchWithAuth(`${API_URL}/ocr/split-and-extract-async`, {
      method: 'POST',
      body: JSON.stringify({ documentUrl }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * GET /ocr/batch/:id/progress — Progrès d'un job batch
   */
  getBatchProgress: async (batchImportId: string) => {
    const response = await fetchWithAuth(`${API_URL}/ocr/batch/${batchImportId}/progress`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * SSE /ocr/batch/:id/progress/stream — URL pour EventSource
   */
  getBatchProgressStreamUrl: (batchImportId: string) => {
    return `${API_URL}/ocr/batch/${batchImportId}/progress/stream`;
  },
};

// ============================================
// 🎵 CHORUS PRO API
// ============================================
export const chorusProAPI = {
  getInvoices: async (status?: string) => {
    const params = new URLSearchParams();
    if (status && status !== "all") params.append("status", status);

    const url = params.toString()
      ? `${API_URL}/compliance/chorus-pro/received?${params}`
      : `${API_URL}/compliance/chorus-pro/received`;
    const response = await fetchWithAuth(url);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des factures Chorus Pro");
    }

    return safeJsonParse(response);
  },

  getStatistics: async () => {
    const response = await fetchWithAuth(`${API_URL}/compliance/chorus-pro/statistics`);


    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération des statistiques: ${response.statusText}`);
    }

    return safeJsonParse(response);
  },

  approveInvoice: async (invoiceId: string, comment: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/compliance/chorus-pro/received/${invoiceId}/accept`,
      {
        method: "POST",
        body: JSON.stringify({ comment }),
      }
    );

    if (!response.ok) {
      throw new Error("Erreur lors de l'approbation de la facture");
    }

    return safeJsonParse(response);
  },

  rejectInvoice: async (invoiceId: string, reason: string, comment: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/compliance/chorus-pro/received/${invoiceId}/reject`,
      {
        method: "POST",
        body: JSON.stringify({ reason, comment }),
      }
    );

    if (!response.ok) {
      throw new Error("Erreur lors du rejet de la facture");
    }

    return safeJsonParse(response);
  },

  downloadInvoice: async (invoiceId: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/compliance/chorus-pro/received/${invoiceId}/download`,
      {
      }
    );

    if (!response.ok) {
      throw new Error("Erreur lors du téléchargement de la facture");
    }

    return response.blob();
  },
};

// ============================================
// ⚙️ CHORUS PRO SETTINGS API
// ============================================
export const chorusProSettingsAPI = {
  /**
   * Récupérer la configuration Chorus Pro de la société
   */
  getConfig: async () => {
    const response = await fetchWithAuth(`${API_URL}/compliance/settings/chorus-pro`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération de la configuration Chorus Pro");
    }

    return safeJsonParse(response);
  },

  /**
   * Créer ou mettre à jour la configuration Chorus Pro
   */
  upsertConfig: async (config: {
    isEnabled: boolean;
    clientId?: string;
    clientSecret?: string;
    siret?: string;
    environment: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/compliance/settings/chorus-pro`, {
      method: "POST",
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la sauvegarde de la configuration Chorus Pro");
    }

    return safeJsonParse(response);
  },

  /**
   * Tester la connexion Chorus Pro avec les credentials fournis
   */
  testConnection: async (
    clientId: string,
    clientSecret: string,
    environment: string
  ) => {
    const response = await fetchWithAuth(`${API_URL}/compliance/settings/chorus-pro/test`, {
      method: "POST",
      body: JSON.stringify({ clientId, clientSecret, environment }),
    });

    if (!response.ok) {
      throw new Error("Erreur lors du test de connexion Chorus Pro");
    }

    return safeJsonParse(response);
  },

  /**
   * Désactiver Chorus Pro pour la société
   */
  disableConfig: async () => {
    const response = await fetchWithAuth(`${API_URL}/compliance/settings/chorus-pro`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la désactivation de Chorus Pro");
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 📊 ACCOUNTANT EXPORT API
// ============================================
export const accountantExportAPI = {
  /**
   * Exporter les écritures comptables dans différents formats
   * @param startDate - Date de début (YYYY-MM-DD)
   * @param endDate - Date de fin (YYYY-MM-DD)
   * @param format - Format d'export (fec, sage, cegid, ebp, csv, excel)
   */
  exportInvoices: async (
    startDate: string,
    endDate: string,
    format: string
  ): Promise<Blob> => {
    // ✅ CORRIGÉ: Utiliser le bon endpoint /business-metrics/export/invoices
    const endpoint = `/business-metrics/export/invoices?start=${startDate}&end=${endDate}&format=${format}`;
    const response = await fetchWithAuth(`${API_URL}${endpoint}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return response.blob();
  },
};

// ============================================
// 🏢 COMPANY CREATION API
// ============================================
export const companyCreationAPI = {
  searchSirene: async (siret: string) => {
    // Utilise le proxy backend pour éviter les problèmes CORS
    const response = await safeFetch(`${API_URL}/core/sirene/${siret}`, {
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la recherche SIRENE");
    }

    const result = await safeJsonParse(response);
    // Le proxy renvoie { success, data }, on extrait data
    return result.data;
  },

  searchFallback: async (siret: string) => {
    // Utilise le proxy backend pour éviter les problèmes CORS
    const response = await safeFetch(`${API_URL}/core/sirene/search/${siret}`, {
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la recherche alternative");
    }

    const result = await safeJsonParse(response);
    // Le proxy renvoie { success, data }, on extrait data
    return result.data;
  },

  create: async (companyData: any) => {
    // Mapper les champs du frontend vers les noms attendus par le backend
    const mappedData = {
      legalName: companyData.name,
      tradeName: companyData.name, // Même valeur par défaut
      registrationNumber: companyData.siret,
      vatNumber: companyData.vatNumber,
      taxCountryCode: 'FR',
      addressLine1: companyData.address,
      addressLine2: null,
      zipCode: companyData.postalCode,
      city: companyData.city,
      countryCode: 'FR',
      email: companyData.email || null, // ✅ Email existe dans Company model
      phone: companyData.phone || null, // ✅ Phone existe dans Company model
      iban: null,
      bic: null,
      defaultCurrencyCode: 'EUR',
    };

    const response = await fetchWithAuth(`${API_URL}/core/companies`, {
      method: "POST",
      body: JSON.stringify(mappedData),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);

      // Créer une erreur avec le message exact du backend
      const error = new Error(parseApiError(errorData));
      // Ajouter le code HTTP pour des vérifications supplémentaires
      (error as any).statusCode = response.status;

      throw error;
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 🔄 THREE WAY MATCHING API
// ============================================
export const threeWayMatchingAPI = {
  getMatches: async () => {
    const response = await fetchWithAuth(`${API_URL}/expenses/three-way-match/matches`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des correspondances");
    }

    return safeJsonParse(response);
  },

  getStatistics: async () => {
    const response = await fetchWithAuth(`${API_URL}/expenses/three-way-match/statistics`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des statistiques");
    }

    return safeJsonParse(response);
  },

  uploadFile: async (file: File, type: "po" | "gr" | "invoice") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);

    const response = await fetchWithAuth(`${API_URL}/expenses/three-way-match/upload`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Erreur lors de l'upload du fichier");
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 🗄️ PROBATORY ARCHIVE API
// ============================================
export const probatoryArchiveAPI = {
  getDocuments: async (type?: string) => {
    const user = useAuthStore.getState().user;
    const companyId = user?.companyId;

    if (!companyId) {
      throw new Error("companyId manquant - utilisateur non authentifié");
    }

    const params = new URLSearchParams();
    params.append("companyId", companyId);
    if (type && type !== "all") params.append("type", type);

    const response = await fetchWithAuth(`${API_URL}/compliance/archive/documents?${params}`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des documents archivés");
    }

    return safeJsonParse(response);
  },

  // ✅ CORRIGÉ: /statistics au lieu de /stats
  getStatistics: async () => {
    const user = useAuthStore.getState().user;
    const companyId = user?.companyId;

    if (!companyId) {
      throw new Error("companyId manquant - utilisateur non authentifié");
    }

    const response = await fetchWithAuth(`${API_URL}/compliance/archive/statistics?companyId=${companyId}`);


    if (!response.ok) {
      throw new Error(
        "Erreur lors de la récupération des statistiques d'archive"
      );
    }

    return safeJsonParse(response);
  },

  // ✅ CORRIGÉ: documentId est un UUID (string), pas un number
  verifyDocument: async (documentId: string) => {
    const user = useAuthStore.getState().user;
    const companyId = user?.companyId;

    if (!companyId) {
      throw new Error("companyId manquant - utilisateur non authentifié");
    }

    const response = await fetchWithAuth(
      `${API_URL}/compliance/archive/documents/${documentId}/verify?companyId=${companyId}`,
      {
        method: "POST",
      }
    );

    if (!response.ok) {
      throw new Error("Erreur lors de la vérification du document");
    }

    return safeJsonParse(response);
  },

  // ⚠️ DEPRECATED: Le backend ne fournit plus d'endpoint /download
  // Les documents sont stockés sur S3, utiliser archiveKey pour télécharger
  downloadDocument: async (documentId: string) => {
    console.warn('downloadDocument() est deprecated - utiliser directement archiveKey S3');

    const user = useAuthStore.getState().user;
    const companyId = user?.companyId;

    if (!companyId) {
      throw new Error("companyId manquant - utilisateur non authentifié");
    }

    // Récupérer d'abord l'archive pour obtenir archiveKey
    const archiveResponse = await fetchWithAuth(
      `${API_URL}/compliance/archive/documents/${documentId}?companyId=${companyId}`,
      {
      }
    );

    if (!archiveResponse.ok) {
      throw new Error("Erreur lors de la récupération de l'archive");
    }

    const archiveData = await archiveResponse.json();

    throw new Error("Téléchargement non implémenté - utiliser archiveKey: " + archiveData.data.archiveKey);
  },
};

// ============================================
// 🏦 BANK RECONCILIATION API
// ============================================
export const bankReconciliationAPI = {
  getMatches: async () => {
    const response = await fetchWithAuth(`${API_URL}/bank-reconciliation/matches`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des correspondances");
    }

    return safeJsonParse(response);
  },

  getStatistics: async () => {
    const response = await fetchWithAuth(`${API_URL}/bank-reconciliation/statistics`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des statistiques");
    }

    return safeJsonParse(response);
  },

  uploadFile: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetchWithAuth(`${API_URL}/bank-reconciliation/upload`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ error: "Erreur lors de l'upload" }));
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  approveMatch: async (matchId: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/bank-reconciliation/matches/${matchId}/approve`,
      { method: "POST" }
    );

    if (!response.ok) {
      throw new Error("Erreur lors de l'approbation de la correspondance");
    }

    return safeJsonParse(response);
  },

  rejectMatch: async (matchId: string, reason: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/bank-reconciliation/matches/${matchId}/reject`,
      {
        method: "POST",
        body: JSON.stringify({ reason }),
      }
    );

    if (!response.ok) {
      throw new Error("Erreur lors du rejet de la correspondance");
    }

    return safeJsonParse(response);
  },

  getTransactions: async (status?: string) => {
    const url = status
      ? `${API_URL}/bank-reconciliation/transactions?status=${encodeURIComponent(status)}`
      : `${API_URL}/bank-reconciliation/transactions`;
    const response = await fetchWithAuth(url);

    if (!response.ok) {
      throw new Error("Erreur lors de la recuperation des transactions");
    }

    return safeJsonParse(response);
  },

  processReconciliation: async () => {
    const response = await fetchWithAuth(`${API_URL}/bank-reconciliation/process`, {
      method: "POST",
    });

    if (!response.ok) {
      throw new Error("Erreur lors du rapprochement automatique");
    }

    return safeJsonParse(response);
  },

  deleteTransaction: async (transactionId: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/bank-reconciliation/transactions/${transactionId}`,
      { method: "DELETE" }
    );

    if (!response.ok) {
      throw new Error("Erreur lors de la suppression de la transaction");
    }

    return safeJsonParse(response);
  },

  deleteAllTransactions: async () => {
    const response = await fetchWithAuth(`${API_URL}/bank-reconciliation/transactions`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la suppression des transactions");
    }

    return safeJsonParse(response);
  },

  createManualMatch: async (transactionId: string, invoiceId: string) => {
    const response = await fetchWithAuth(`${API_URL}/bank-reconciliation/matches/manual`, {
      method: "POST",
      body: JSON.stringify({ transactionId, invoiceId }),
    });

    if (!response.ok) {
      throw new Error("Erreur lors du rapprochement manuel");
    }

    return safeJsonParse(response);
  },

  getUnpaidInvoices: async () => {
    const response = await fetchWithAuth(`${API_URL}/billing/invoices?status=SENT,OVERDUE`);

    if (!response.ok) {
      throw new Error("Erreur lors de la recuperation des factures");
    }

    return safeJsonParse(response);
  },

  detectAnomalies: async () => {
    const response = await fetchWithAuth(`${API_URL}/bank-reconciliation/anomalies`);

    if (!response.ok) {
      throw new Error("Erreur lors de la detection des anomalies");
    }

    return safeJsonParse(response);
  },

  // Open Banking Connections — companyId comes from JWT (ISO 27001 A.9.4.1)
  getConnections: async () => {
    const response = await fetchWithAuth(`${API_URL}/bank-reconciliation/connections`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des connexions bancaires");
    }

    return safeJsonParse(response);
  },

  createConnection: async (data: { provider: string; bankName: string; bankAccountId?: string }) => {
    const response = await fetchWithAuth(`${API_URL}/bank-reconciliation/connections`, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ error: "Erreur lors de la création" }));
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  syncConnection: async (connectionId: string, startDate?: string, endDate?: string) => {
    const body: Record<string, string> = {};
    if (startDate) body.startDate = startDate;
    if (endDate) body.endDate = endDate;

    const response = await fetchWithAuth(`${API_URL}/bank-reconciliation/connections/${encodeURIComponent(connectionId)}/sync`, {
      method: "POST",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la synchronisation");
    }

    return safeJsonParse(response);
  },

  deleteConnection: async (connectionId: string) => {
    const response = await fetchWithAuth(`${API_URL}/bank-reconciliation/connections/${encodeURIComponent(connectionId)}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la suppression de la connexion");
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 💰 EXPENSES API
// ============================================
export const expensesAPI = {
  getAll: async (params?: {
    category?: string;
    status?: string;
    page?: number;
    limit?: number;
    startDate?: string;  // ISO date string for period filter
    endDate?: string;    // ISO date string for period filter
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append("category", params.category);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.startDate) queryParams.append("startDate", params.startDate);
    if (params?.endDate) queryParams.append("endDate", params.endDate);

    const url = queryParams.toString()
      ? `${API_URL}/expenses?${queryParams}`
      : `${API_URL}/expenses`;
    const response = await fetchWithAuth(url);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des dépenses");
    }

    return safeJsonParse(response);
  },

  getStatistics: async (period?: '7d' | '30d' | '90d' | '365d' | 'all') => {
    const queryParams = new URLSearchParams();
    if (period && period !== 'all') {
      queryParams.append("period", period);
    } else if (period === 'all') {
      // Pour "all", on utilise 365d * 10 = 10 ans (ou on pourrait ajouter un support backend)
      queryParams.append("period", "365d");
    }

    const url = queryParams.toString()
      ? `${API_URL}/expenses/stats?${queryParams}`
      : `${API_URL}/expenses/stats`;
    const response = await fetchWithAuth(url);


    if (!response.ok) {
      throw new Error(
        "Erreur lors de la récupération des statistiques de dépenses"
      );
    }

    return safeJsonParse(response);
  },

  create: async (expenseData: any) => {
    const response = await fetchWithAuth(`${API_URL}/expenses`, {
      method: "POST",
      body: JSON.stringify(expenseData),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(
        errorData.error?.message || errorData.message || "Erreur lors de la création de la dépense"
      );
    }

    return safeJsonParse(response);
  },

  update: async (expenseId: number, expenseData: any) => {
    const response = await fetchWithAuth(`${API_URL}/expenses/${expenseId}`, {
      method: "PUT",
      body: JSON.stringify(expenseData),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },

  getById: async (expenseId: string) => {
    const response = await fetchWithAuth(`${API_URL}/expenses/${expenseId}`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération de la dépense");
    }

    return safeJsonParse(response);
  },

  // ✅ Méthode générique pour changer le statut d'une dépense
  updateStatus: async (expenseId: string, status: string, options?: { reason?: string; paidAt?: string; paymentMethod?: string }) => {
    const response = await fetchWithAuth(`${API_URL}/expenses/${expenseId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status, ...options }),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(
        errorData.error?.message || errorData.message || "Erreur lors du changement de statut"
      );
    }

    return safeJsonParse(response);
  },

  markAsPaid: async (expenseId: number) => {
    const response = await fetchWithAuth(`${API_URL}/expenses/${expenseId}/mark-paid`, {
      method: "PATCH",
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },

  delete: async (expenseId: number) => {
    const response = await fetchWithAuth(`${API_URL}/expenses/${expenseId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },

  // ==================== SUPPLIER PREFERENCES (OCR Auto-Categorization) ====================

  /**
   * Save (upsert) a supplier preference for OCR auto-categorization.
   * Called when user clicks "Toujours appliquer" in SmartCategorySelector.
   */
  saveSupplierPreference: async (data: {
    supplierName: string;
    defaultCategoryId?: string;
    defaultAccountCode?: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/expenses/supplier-preferences`, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },

  /**
   * Get a supplier preference by supplier name.
   */
  getSupplierPreference: async (supplierName: string) => {
    const encodedName = encodeURIComponent(supplierName);
    const response = await fetchWithAuth(`${API_URL}/expenses/supplier-preferences/${encodedName}`);


    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },

  /**
   * List all supplier preferences for the company.
   */
  listSupplierPreferences: async () => {
    const response = await fetchWithAuth(`${API_URL}/expenses/supplier-preferences`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des préférences fournisseur");
    }

    return safeJsonParse(response);
  },

  // ==================== ANOMALY DETECTION (ML) - Sprint 3.1 US-3.1.4 ====================

  /**
   * Detecter les anomalies dans une depense
   */
  detectAnomaly: async (expenseId: string) => {
    const response = await fetchWithAuth(`${API_URL}/expenses/detect-anomaly`, {
      method: "POST",
      body: JSON.stringify({ expenseId }),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },

  /**
   * Detecter les anomalies dans plusieurs depenses (batch)
   */
  detectAnomaliesBatch: async (expenseIds?: string[]) => {
    const response = await fetchWithAuth(`${API_URL}/expenses/detect-anomalies-batch`, {
      method: "POST",
      body: JSON.stringify({ expenseIds }),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },

  /**
   * Recuperer les donnees historiques des depenses
   */
  getHistoricalData: async (categories?: string[]) => {
    const queryParams = new URLSearchParams();
    if (categories?.length) {
      queryParams.append("categories", categories.join(","));
    }

    const url = queryParams.toString()
      ? `${API_URL}/expenses/historical-data?${queryParams}`
      : `${API_URL}/expenses/historical-data`;

    const response = await fetchWithAuth(url);


    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },

  /**
   * Recuperer les donnees budget pour un projet
   */
  getBudgetData: async (projectId: string, category?: string) => {
    const queryParams = new URLSearchParams();
    queryParams.append("projectId", projectId);
    if (category) {
      queryParams.append("category", category);
    }

    const response = await fetchWithAuth(`${API_URL}/expenses/budget-data?${queryParams}`);


    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 📊 ACCOUNTING API
// ============================================
export const accountingAPI = {
  getDashboard: async () => {
    const response = await fetchWithAuth(`${API_URL}/accounting/dashboard`);


    if (!response.ok) {
      throw new Error(
        "Erreur lors de la récupération du tableau de bord comptable"
      );
    }

    return safeJsonParse(response);
  },

  getIncomeStatement: async (months: number = 6) => {
    const response = await fetchWithAuth(
      `${API_URL}/accounting/income-statement?months=${months}`,
      {
      }
    );

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération du compte de résultat");
    }

    return safeJsonParse(response);
  },

  getExpensesStatistics: async () => {
    const response = await fetchWithAuth(`${API_URL}/expenses/statistics`);


    if (!response.ok) {
      throw new Error(
        "Erreur lors de la récupération des statistiques de dépenses"
      );
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 📈 BUSINESS METRICS API
// ============================================

/**
 * Métriques business pour le dashboard
 * Backend: /api/business-metrics/*
 */
export const businessMetricsAPI = {
  /**
   * GET /api/business-metrics/revenue
   * Métriques de revenus (CA réalisé, prévisionnel, en attente)
   */
  getRevenue: async () => {
    const response = await fetchWithAuth(`${API_URL}/business-metrics/revenue`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * GET /api/business-metrics/revenue/evolution
   * Évolution des revenus sur N mois
   */
  getRevenueEvolution: async (months: number = 6) => {
    const response = await fetchWithAuth(
      `${API_URL}/business-metrics/revenue/evolution?months=${months}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * GET /api/business-metrics/commercial
   * Métriques commerciales (devis, factures, taux de conversion)
   */
  getCommercial: async () => {
    const response = await fetchWithAuth(`${API_URL}/business-metrics/commercial`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * GET /api/treasury/metrics
   * Métriques de trésorerie (montants payés, en attente, en retard)
   * NOTE: Migré depuis /business-metrics/treasury vers /treasury/metrics
   */
  getTreasury: async () => {
    const response = await fetchWithAuth(`${API_URL}/treasury/metrics`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * GET /api/business-metrics/products/performance
   * Performance des produits (top ventes)
   */
  getProductsPerformance: async (limit: number = 5) => {
    const response = await fetchWithAuth(
      `${API_URL}/business-metrics/products/performance?limit=${limit}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * GET /api/business-metrics/products/categories
   * Répartition des produits par catégories
   */
  getProductsCategories: async () => {
    const response = await fetchWithAuth(`${API_URL}/business-metrics/products/categories`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * GET /api/business-metrics/unified
   * Statistiques financières unifiées - SOURCE DE VÉRITÉ
   * @param period - '7d' | '30d' | '90d' | '365d' | 'all' (default: '30d')
   * @see docs/SPRINT_FINANCIAL_ARCHITECTURE.md
   */
  getUnified: async (period: '7d' | '30d' | '90d' | '365d' | 'all' = '30d') => {
    const response = await fetchWithAuth(
      `${API_URL}/business-metrics/unified?period=${period}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * POST /api/business-metrics/cache/invalidate
   * Invalider le cache des statistiques financières
   */
  invalidateCache: async () => {
    const response = await fetchWithAuth(`${API_URL}/business-metrics/cache/invalidate`, {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 📊 MONITORING API
// ============================================
// ============================================
// 👤 USERS API
// ============================================
export const usersAPI = {
  getAll: async () => {
    const response = await fetchWithAuth(`${API_URL}/core/users`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des utilisateurs");
    }

    return safeJsonParse(response);
  },

  create: async (userData: any) => {
    const response = await fetchWithAuth(`${API_URL}/core/users`, {
      method: "POST",
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      // Le backend renvoie { success: false, error: { message: "..." } }
      const message = errorData?.error?.message || errorData?.message || "Erreur lors de la création de l'utilisateur";
      throw new Error(message);
    }

    return safeJsonParse(response);
  },

  update: async (userId: number, userData: any) => {
    const response = await fetchWithAuth(`${API_URL}/core/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },

  delete: async (userId: number) => {
    const response = await fetchWithAuth(`${API_URL}/core/users/${userId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 🌐 PUBLIC INVOICES API (SANS AUTHENTIFICATION)
// ============================================
export const publicInvoicesAPI = {
  getInvoice: async (token: string) => {
    const response = await safeFetch(`${API_URL}/public/invoices/${token}`, {
      // ✅ API publique - PAS de header Authorization
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (response.status === 410) {
        throw new Error("Ce lien a expiré ou est invalide");
      }
      throw new Error("Impossible de charger la facture");
    }

    return safeJsonParse(response);
  },

  createCheckoutSession: async (token: string) => {
    const response = await safeFetch(
      `${API_URL}/public/invoices/${token}/create-checkout-session`,
      {
        method: "POST",
        // ✅ API publique - PAS de header Authorization
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },

  createPayPalOrder: async (token: string) => {
    const response = await safeFetch(
      `${API_URL}/public/invoices/${token}/create-paypal-order`,
      {
        method: "POST",
        // ✅ API publique - PAS de header Authorization
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },

  // ============================================
  // 🆕 NEW PAYMENT ENDPOINTS (Phase 1 Backend)
  // ============================================

  /**
   * Récupérer les options de paiement disponibles pour une facture
   */
  getPaymentOptions: async (token: string) => {
    const response = await safeFetch(`${API_URL}/public/invoices/${token}/payment-options`, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (response.status === 410) {
        throw new Error("Ce lien a expiré ou est invalide");
      }
      if (response.status === 409) {
        throw new Error("Cette facture a déjà été payée");
      }
      throw new Error("Impossible de charger les options de paiement");
    }

    return safeJsonParse(response);
  },

  /**
   * Initier un paiement Stripe (PaymentIntent avec Connect)
   */
  initiateStripePayment: async (token: string) => {
    const response = await safeFetch(`${API_URL}/public/invoices/${token}/pay/stripe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(errorData.error?.message || "Erreur lors de l'initialisation du paiement Stripe");
    }

    return safeJsonParse(response);
  },

  /**
   * Initier un paiement PayPal (créer une commande)
   */
  initiatePayPalPayment: async (token: string, returnUrl: string, cancelUrl: string) => {
    const response = await safeFetch(`${API_URL}/public/invoices/${token}/pay/paypal`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ returnUrl, cancelUrl }),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(errorData.error?.message || "Erreur lors de l'initialisation du paiement PayPal");
    }

    return safeJsonParse(response);
  },

  /**
   * Capturer un paiement PayPal après approbation
   */
  capturePayPalPayment: async (token: string, orderId: string) => {
    const response = await safeFetch(`${API_URL}/public/invoices/${token}/pay/paypal/capture`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ orderId }),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(errorData.error?.message || "Erreur lors de la capture du paiement PayPal");
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 🔗 PUBLIC LINKS API
// ============================================
export const publicLinksAPI = {
  getQuoteLink: async (quoteId: string) => {
    const response = await fetchWithAuth(`${API_URL}/public-links/quotes/${quoteId}/links`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération du lien");
    }

    return safeJsonParse(response);
  },

  createQuoteLink: async (quoteId: string) => {
    const response = await fetchWithAuth(`${API_URL}/public-links/quotes/${quoteId}/link`, {
      method: "POST",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  sendQuoteEmail: async (quoteId: string, data: { to: string; subject: string; body: string }) => {
    const response = await fetchWithAuth(`${API_URL}/billing/quotes/${quoteId}/send`, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  // ✅ Méthodes pour les factures (symétrie avec les devis)
  getInvoiceLink: async (invoiceId: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/public-links/invoices/${invoiceId}/links`,
      {
      }
    );

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération du lien");
    }

    return safeJsonParse(response);
  },

  createInvoiceLink: async (invoiceId: string, expiresInDays: number = 90) => {
    const response = await fetchWithAuth(
      `${API_URL}/public-links/invoices/${invoiceId}/link`,
      {
        method: "POST",
        body: JSON.stringify({ expiresInDays }),
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  sendInvoiceEmail: async (invoiceId: string, data: { to: string; subject: string; body: string }) => {
    const response = await fetchWithAuth(
      `${API_URL}/billing/invoices/${invoiceId}/send`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  // ✅ Dashboard centralisé des liens publics
  getDashboard: async () => {
    const response = await fetchWithAuth(`${API_URL}/public-links/dashboard`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération du dashboard");
    }

    return safeJsonParse(response);
  },

  revokeLink: async (token: string) => {
    const response = await fetchWithAuth(`${API_URL}/public-links/links/${token}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 📥 IMPORT CSV API (Clients & Produits)
// ============================================
export const importAPI = {
  // Télécharger le template CSV clients
  getClientTemplate: async (): Promise<Blob> => {
    const response = await fetchWithAuth(`${API_URL}/catalog/import/clients/template`);


    if (!response.ok) {
      throw new Error("Erreur lors du téléchargement du template");
    }

    return response.blob();
  },

  // Télécharger le template CSV produits
  getProductTemplate: async (): Promise<Blob> => {
    const response = await fetchWithAuth(`${API_URL}/catalog/import/products/template`);


    if (!response.ok) {
      throw new Error("Erreur lors du téléchargement du template");
    }

    return response.blob();
  },

  // Valider un fichier CSV clients avant import
  validateClients: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetchWithAuth(`${API_URL}/catalog/import/clients/validate`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  // Valider un fichier CSV produits avant import
  validateProducts: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetchWithAuth(`${API_URL}/catalog/import/products/validate`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  // Importer les clients depuis un fichier CSV
  importClients: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetchWithAuth(`${API_URL}/catalog/import/clients`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  // Importer les produits depuis un fichier CSV
  importProducts: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetchWithAuth(`${API_URL}/catalog/import/products`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 💳 STRIPE CONNECT API
// ============================================
export const stripeConnectAPI = {
  getStatus: async () => {
    const response = await fetchWithAuth(`${API_URL}/payments/stripe-connect/status`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération du statut Stripe");
    }

    return safeJsonParse(response);
  },

  createAccount: async (data: { name: string; email: string; country?: string; businessType?: string }) => {
    const response = await fetchWithAuth(`${API_URL}/payments/stripe-connect/accounts`, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  getOnboardingLink: async () => {
    const response = await fetchWithAuth(`${API_URL}/payments/stripe-connect/onboarding-link`, {
      method: "POST",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  getDashboardLink: async () => {
    const response = await fetchWithAuth(`${API_URL}/payments/stripe-connect/dashboard-link`, {
      method: "POST",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 💰 PAYPAL API
// ============================================
export const paypalAPI = {
  getStatus: async () => {
    const response = await fetchWithAuth(`${API_URL}/payments/paypal/status`);


    if (!response.ok) {
      throw new Error("Erreur lors de la récupération du statut PayPal");
    }

    return safeJsonParse(response);
  },

  configure: async (data: {
    clientId: string;
    clientSecret: string;
    merchantId?: string | null;
    mode: "sandbox" | "live";
  }) => {
    const response = await fetchWithAuth(`${API_URL}/payments/paypal/configure`, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  disable: async () => {
    const response = await fetchWithAuth(`${API_URL}/payments/paypal/disable`, {
      method: "POST",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 💳 SUBSCRIPTIONS API
// ============================================
export const subscriptionsAPI = {
  getConfig: async () => {
    const response = await fetchWithAuth(`${API_URL}/subscriptions/config`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération de la config Stripe");
    }
    const result = await safeJsonParse(response);
    return result.data || result;
  },

  getPlans: async () => {
    const response = await fetchWithAuth(`${API_URL}/subscriptions/plans`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des plans");
    }
    return safeJsonParse(response);
  },

  getCurrent: async (): Promise<TenantSubscription | null> => {
    const response = await fetchWithAuth(`${API_URL}/subscriptions/current`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération de l'abonnement actuel");
    }
    const data = await safeJsonParse(response);
    // Backend retourne { success: true, data: subscription } ou { subscription: null }
    return data.data || data.subscription || null;
  },

  getQuotas: async (): Promise<{
    plan: string;
    planName: string;
    priceMonthly: number;
    features: Record<string, boolean>;
    status: string;
    currentPeriodEnd?: Date;
    trialEndsAt?: Date;
    cancelAtPeriodEnd: boolean;
    quotas: Array<{
      metric: string;
      limit: number;
      used: number;
      remaining: number;
      percentage: number;
    }>;
  }> => {
    const response = await fetchWithAuth(`${API_URL}/subscriptions/quotas`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des quotas");
    }
    const data = await safeJsonParse(response);
    // Backend retourne { success: true, data: { plan, planName, priceMonthly, features, status, quotas: [...] } }
    return data.data || {
      plan: 'FREE',
      planName: 'Gratuit',
      priceMonthly: 0,
      features: {},
      status: 'NONE',
      trialEndsAt: null,
      cancelAtPeriodEnd: false,
      quotas: [],
    };
  },

  checkQuota: async (type: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/subscriptions/check-quota/${type}`,
      {
      }
    );
    if (!response.ok) {
      throw new Error("Erreur lors de la vérification du quota");
    }
    return safeJsonParse(response);
  },

  createCheckoutSession: async (planCode: string, successUrl: string, cancelUrl: string, billingCycle: 'monthly' | 'annual' = 'monthly'): Promise<{ url: string }> => {
    const response = await fetchWithAuth(`${API_URL}/subscriptions/checkout-session`, {
      method: "POST",
      body: JSON.stringify({ planCode, billingCycle, successUrl, cancelUrl }),
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }
    const result = await safeJsonParse(response);
    return result.data || result;
  },

  sync: async () => {
    const response = await fetchWithAuth(`${API_URL}/subscriptions/sync`, {
      method: "POST",
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }
    const data = await safeJsonParse(response);
    return data.data || data;
  },

  getHistory: async (): Promise<SubscriptionEvent[]> => {
    const response = await fetchWithAuth(`${API_URL}/subscriptions/history`);

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération de l'historique");
    }
    const data = await safeJsonParse(response);
    // Backend retourne { success: true, data: history[], count: number }
    return data.data || [];
  },

  createBillingPortalSession: async (returnUrl: string): Promise<{ url: string }> => {
    const response = await fetchWithAuth(`${API_URL}/subscriptions/portal-session`, {
      method: "POST",
      body: JSON.stringify({ returnUrl }),
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }
    const data = await safeJsonParse(response);
    return data.data;
  },
};

// ============================================
export const sessionsAPI = {
  refresh: async () => {
    // Backend accepte le refresh token depuis cookies (prioritaire) OU body
    // On envoie le token dans le body seulement s'il est valide (pas un placeholder)
    const refreshToken = getRefreshToken();

    // Vérifier si c'est un vrai token (pas le placeholder 'HTTPONLY_COOKIE')
    const isValidToken = refreshToken && refreshToken !== 'HTTPONLY_COOKIE' && refreshToken.length > 20;

    // Construire le body - vide si pas de token valide (le cookie HttpOnly sera utilisé)
    const body = isValidToken ? JSON.stringify({ refreshToken }) : '{}';

    const response = await safeFetch(`${API_URL}/core/auth/refresh`, {
      method: "POST",
      credentials: "include", // Envoie automatiquement les cookies HttpOnly si présents
      headers: { "Content-Type": "application/json" },
      body,
    });

    if (!response.ok) {
      // Si le refresh token est invalide, nettoyer la session
      if (response.status === 401 || response.status === 403) {
        clearSession();
      }
      throw new Error("Échec du refresh du token");
    }

    const json = await safeJsonParse(response);
    // Backend retourne { success: true, data: { accessToken, expiresIn, refreshToken } }
    const result = json.data || json;

    // Si le backend retourne un nouveau refresh token (rotation), le stocker
    if (result.refreshToken && result.refreshToken.length > 20) {
      setRefreshToken(result.refreshToken);
    }

    return result;
  },

  activity: async () => {
    // Note: activity/heartbeat pourrait être implémenté plus tard si nécessaire
    // Pour l'instant, on retourne un succès silencieux
    return Promise.resolve({ success: true });
  },

  revoke: async () => {
    const refreshToken = getRefreshToken();

    // Si pas de refresh token, pas besoin d'appeler le backend
    if (!refreshToken) {
      return { success: true };
    }

    const response = await fetchWithAuth(`${API_URL}/core/auth/logout`, {
      method: "POST",
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      // Si le token est déjà invalide (401), on considère que c'est OK
      if (response.status === 401 || response.status === 400) {
        return { success: true };
      }
      throw new Error("Erreur lors de la révocation de la session");
    }

    return safeJsonParse(response);
  },
};

// ============================================
// TRADES API (Trade References)
// ============================================

export const tradesAPI = {
  /**
   * GET /api/core/trades — Liste tous les métiers actifs groupés par catégorie
   */
  getAll: async () => {
    const response = await fetchWithAuth(`${API_URL}/core/trades`);
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }
    const json = await safeJsonParse(response);
    return json.data;
  },

  /**
   * GET /api/core/trades/suggest?apeCode=xxx — Suggestion par code APE
   */
  suggestFromAPE: async (apeCode: string) => {
    const response = await fetchWithAuth(`${API_URL}/core/trades/suggest?apeCode=${encodeURIComponent(apeCode)}`);
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }
    const json = await safeJsonParse(response);
    return json.data;
  },

  /**
   * GET /api/core/trades/:code — Détail d'un métier
   */
  getByCode: async (code: string) => {
    const response = await fetchWithAuth(`${API_URL}/core/trades/${encodeURIComponent(code)}`);
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }
    const json = await safeJsonParse(response);
    return json.data;
  },
};

// ============================================
export const companiesAPI = {
  getAll: async () => {
    const response = await fetchWithAuth(`${API_URL}/core/companies`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  getById: async (companyId: string) => {  // ✅ CORRIGÉ: UUID string
    const response = await fetchWithAuth(`${API_URL}/core/companies/${companyId}`);


    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Société non trouvée");
      }
      if (response.status === 401) {
        throw new Error("Non autorisé");
      }
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiCompany = await safeJsonParse(response);
    return apiCompany.data || apiCompany;
  },

  update: async (companyId: string, data: any) => {  // ✅ CORRIGÉ: UUID string
    const response = await fetchWithAuth(`${API_URL}/core/companies/${companyId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      // Gérer le format d'erreur avec tableau errors (Zod validation)
      if (errorData.errors && Array.isArray(errorData.errors)) {
        const messages = errorData.errors.map(
          (e: { field?: string; message?: string }) => `${e.field}: ${e.message}`
        ).join(', ');
        throw new Error(messages);
      }
      throw new Error(parseApiError(errorData));
    }

    const apiCompany = await safeJsonParse(response);
    return apiCompany.data || apiCompany;
  },

  uploadLogo: async (companyId: string, formData: FormData) => {
    // ✅ CORRIGÉ: Utilise /companies/:id/logo au lieu de /companies/upload-logo
    const response = await fetchWithAuth(`${API_URL}/core/companies/${companyId}/logo`, {
      method: "POST",
      body: formData, // FormData gère son propre Content-Type
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * Créer une nouvelle société (Tenant Admin)
   */
  create: async (data: {
    legalName: string;
    tradeName?: string;
    registrationNumber: string;
    vatNumber?: string;
    addressLine1?: string;
    zipCode?: string;
    city?: string;
    countryCode?: string;
    email?: string;
    phone?: string;
    apeCode?: string;
    businessType?: string;
    companyTrade?: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/core/companies`, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * Supprimer une société (Tenant Admin/Owner)
   */
  delete: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/core/companies/${companyId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * Récupérer les sociétés accessibles pour l'utilisateur connecté
   * GET /me/companies
   */
  getMyCompanies: async (): Promise<{
    companies: Array<{
      id: string;
      name: string;
      tradeName: string | null;
      siret: string | null;
      logoUrl: string | null;
      role: string;
      isDefault: boolean;
    }>;
    currentCompanyId: string | null;
    quota: {
      current: number;
      limit: number;
      canCreate: boolean;
    };
  }> => {
    const response = await fetchWithAuth(`${API_URL}/core/me/companies`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * Changer de société active
   * POST /me/companies/switch
   */
  switchCompany: async (companyId: string): Promise<{
    success: boolean;
    companyId: string;
    companyName: string;
  }> => {
    const response = await fetchWithAuth(`${API_URL}/core/me/companies/switch`, {
      method: "POST",
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * Récupérer les utilisateurs d'une société
   * GET /companies/:companyId/users
   */
  getCompanyUsers: async (companyId: string): Promise<Array<{
    id: string;
    userId: string;
    email: string;
    fullName: string | null;
    role: string;
    isActive: boolean;
    createdAt: string;
  }>> => {
    const response = await fetchWithAuth(`${API_URL}/core/companies/${companyId}/users`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * Ajouter un utilisateur à une société
   * POST /companies/:companyId/users
   */
  addCompanyUser: async (companyId: string, data: { userId: string; role: string }): Promise<any> => {
    const response = await fetchWithAuth(`${API_URL}/core/companies/${companyId}/users`, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * Modifier le rôle d'un utilisateur dans une société
   * PUT /companies/:companyId/users/:userId
   */
  updateCompanyUserRole: async (companyId: string, userId: string, role: string): Promise<any> => {
    const response = await fetchWithAuth(`${API_URL}/core/companies/${companyId}/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify({ role }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * Retirer l'accès d'un utilisateur à une société
   * DELETE /companies/:companyId/users/:userId
   */
  removeCompanyUser: async (companyId: string, userId: string): Promise<void> => {
    const response = await fetchWithAuth(`${API_URL}/core/companies/${companyId}/users/${userId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }
  },

  /**
   * Récupérer les feature flags de la société courante (basé sur BusinessType)
   * GET /core/company/features
   * ISO 27001 A.9.4.1: tenantId + companyId depuis JWT
   */
  getFeatures: async (): Promise<{
    businessType: string;
    companyTrade: string | null;
    tradeConfig: {
      businessType: string;
      defaultUnits: string[];
      decennaleRequired: boolean;
      reverseChargeEligible: boolean;
      defaultVatRate: number;
      category: string;
    } | null;
    features: {
      unitOfMeasure: boolean;
      referenceSKU: boolean;
      sections: boolean;
      lineDiscount: boolean;
      depositInvoice: boolean;
      decennaleInsurance: boolean;
      latePenalties: boolean;
      reverseChargeVAT: boolean;
      compositeProducts: boolean;
      tieredPricing: boolean;
      shippingCosts: boolean;
      progressInvoice: boolean;
      retentionGuarantee: boolean;
      ouvrageLibrary: boolean;
    };
  }> => {
    const response = await fetchWithAuth(`${API_URL}/core/company/features`);

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },
};

// ============================================
// 🌐 PUBLIC QUOTES API (Sans authentification)
// ============================================
export const publicQuotesAPI = {
  getQuote: async (token: string) => {
    const response = await safeFetch(`${API_URL}/public/quotes/${token}`, {
      // ✅ API publique - PAS de header Authorization
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (response.status === 410) {
        throw new Error("LINK_DEACTIVATED");
      }
      if (response.status === 404) {
        throw new Error("LINK_NOT_FOUND");
      }
      throw new Error("LOAD_ERROR");
    }

    return safeJsonParse(response);
  },

  acceptQuote: async (
    token: string,
    data: {
      acceptedVia: string;
      timestamp: string;
      signature: string | undefined;
      signedByName: string;
      signedByEmail: string | undefined;
    }
  ) => {
    const response = await safeFetch(`${API_URL}/public/quotes/${token}/accept`, {
      method: "POST",
      // ✅ API publique - PAS de header Authorization
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(parseApiError(errorData));
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 📥 CSV IMPORT API
// ============================================
export const csvImportAPI = {
  import: async (
    endpoint: "clients" | "products" | "invoices",
    formData: FormData
  ) => {
    const response = await fetchWithAuth(`${API_URL}/${endpoint}/import-csv`, {
      method: "POST",
      body: formData, // FormData gère son propre Content-Type
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 💳 CREDIT NOTES API - Re-exported from creditNotesAPI.ts
// ============================================
// See ./creditNotesAPI.ts for full implementation
export { creditNotesAPI } from './creditNotesAPI';

// API générique par défaut pour les nouveaux endpoints
// Utilise safeFetch et safeJsonParse pour éviter les crashes
const api = {
  get: async <T = unknown>(url: string): Promise<T> => {
    try {
      const response = await fetchWithAuth(`${API_URL}${url}`, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await safeJsonParse<{ message?: string; error?: string }>(response);
        throw new Error(parseApiError(error));
      }

      return safeJsonParse<T>(response);
    } catch (error) {
      logger.error('[API GET]', url, error);
      throw error;
    }
  },

  post: async <T = unknown>(url: string, data?: unknown): Promise<T> => {
    try {
      const response = await fetchWithAuth(`${API_URL}${url}`, {
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(parseApiError(error));
      }

      return safeJsonParse<T>(response);
    } catch (error) {
      logger.error('[API POST]', url, error);
      throw error;
    }
  },

  put: async <T = unknown>(url: string, data?: unknown): Promise<T> => {
    try {
      const response = await fetchWithAuth(`${API_URL}${url}`, {
        method: "PUT",
        body: data ? JSON.stringify(data) : undefined,
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(parseApiError(error));
      }

      return safeJsonParse<T>(response);
    } catch (error) {
      logger.error('[API PUT]', url, error);
      throw error;
    }
  },

  patch: async <T = unknown>(url: string, data?: unknown): Promise<T> => {
    try {
      const response = await fetchWithAuth(`${API_URL}${url}`, {
        method: "PATCH",
        body: data ? JSON.stringify(data) : undefined,
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(parseApiError(error));
      }

      return safeJsonParse<T>(response);
    } catch (error) {
      logger.error('[API PATCH]', url, error);
      throw error;
    }
  },

  delete: async <T = unknown>(url: string): Promise<T> => {
    try {
      const response = await fetchWithAuth(`${API_URL}${url}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(parseApiError(error));
      }

      return safeJsonParse<T>(response);
    } catch (error) {
      logger.error('[API DELETE]', url, error);
      throw error;
    }
  },
};

// ============================================
// TEMPLATE COLORS API
// ============================================

/**
 * API pour gérer les couleurs de templates d'une société
 */
export const templateColorsAPI = {
  /**
   * GET /api/templates/preferences/:companyId
   * Récupérer les préférences de template d'une société (incluant couleurs)
   */
  getPreferences: async (companyId: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/templates/preferences/${companyId}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * PATCH /api/templates/preferences/:companyId/colors
   * Mettre à jour les couleurs de template
   */
  updateColors: async (companyId: string, colors: {
    colorPrimary?: string;
    colorSecondary?: string;
    useCompanyColors?: boolean;
  }) => {
    const response = await fetchWithAuth(
      `${API_URL}/templates/preferences/${companyId}/colors`,
      {
        method: 'PATCH',
        body: JSON.stringify(colors),
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },
};

// ==================== ACCOUNTING CONNECTORS API ====================

export const accountingConnectorsAPI = {
  /**
   * GET /api/accounting-connectors/config
   * Récupérer la configuration du connecteur comptable
   */
  getConfig: async () => {
    const response = await fetchWithAuth(`${API_URL}/accounting-connectors/config`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * POST /api/accounting-connectors/config
   * Sauvegarder la configuration du connecteur comptable
   */
  saveConfig: async (config: any) => {
    const response = await fetchWithAuth(`${API_URL}/accounting-connectors/config`, {
      method: 'POST',
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * POST /api/accounting-connectors/test
   * Tester la connexion au connecteur comptable
   */
  testConnection: async () => {
    const response = await fetchWithAuth(`${API_URL}/accounting-connectors/test`, {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * POST /api/accounting-connectors/sync
   * Synchroniser manuellement avec le logiciel comptable
   */
  sync: async () => {
    const response = await fetchWithAuth(`${API_URL}/accounting-connectors/sync`, {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * GET /api/accounting-connectors/logs
   * Récupérer les logs de synchronisation
   */
  getLogs: async () => {
    const response = await fetchWithAuth(`${API_URL}/accounting-connectors/logs`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },
};

// ============================================
// 🏗️ PROJECTS API (CRM Artisans)
// ============================================
export const projectsAPI = {
  /**
   * GET /projects - Liste des projets
   */
  getAll: async (companyId: string, options?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    const params = new URLSearchParams({ companyId });
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.status) params.append('status', options.status);

    const response = await fetchWithAuth(`${API_URL}/projects?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/search - Recherche de projets
   */
  search: async (companyId: string, query: string, limit?: number) => {
    const params = new URLSearchParams({ companyId, q: query });
    if (limit) params.append('limit', limit.toString());

    const response = await fetchWithAuth(`${API_URL}/projects/search?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/stats - Statistiques projets
   */
  getStats: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/stats?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/:id - Détails d'un projet
   */
  getById: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${id}?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects - Créer un projet
   */
  create: async (data: {
    companyId: string;
    clientId?: string;
    quoteId?: string;
    name: string;
    reference?: string;
    description?: string;
    address?: string;
    city?: string;
    zipCode?: string;
    startDate?: string;
    endDate?: string;
    estimatedHours?: number;
    estimatedBudget?: number;
    color?: string;
    tags?: string[];
    notes?: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * PUT /projects/:id - Mettre à jour un projet
   */
  update: async (id: string, data: {
    companyId: string;
    name?: string;
    reference?: string;
    description?: string;
    address?: string;
    city?: string;
    zipCode?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    estimatedHours?: number;
    estimatedBudget?: number;
    color?: string;
    tags?: string[];
    notes?: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * DELETE /projects/:id - Supprimer un projet
   */
  delete: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${id}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return true;
  },

  /**
   * PATCH /projects/:id/status - Changer le statut
   */
  updateStatus: async (id: string, companyId: string, status: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ companyId, status }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/:id/margins - Obtenir les marges
   */
  getMargins: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${id}/margins?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/:id/cost-plans - Ajouter un plan de coût
   * Le backend calcule plannedAmountHt si quantity + unitPrice fournis
   */
  addCostPlan: async (projectId: string, data: {
    companyId: string;
    costType: string;
    description?: string;
    // Option 1: Montant direct
    plannedAmountHt?: number;
    // Option 2: Calcul backend depuis quantity × unitPrice
    unit?: string;
    quantity?: number;
    unitPrice?: number;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/cost-plans`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/:id/actual-costs - Ajouter un coût réel
   * Le backend calcule amountTtc depuis amountHt + vatRate
   */
  addActualCost: async (projectId: string, data: {
    companyId: string;
    costType: string;
    description?: string;
    amountHt: number;
    // Option 1: vatRate → backend calcule TTC
    vatRate?: number;
    // Option 2: montants déjà calculés (rétrocompatibilité)
    amountTax?: number;
    amountTtc?: number;
    dateIncurred: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/actual-costs`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  // ==================== ERP BTP API ====================

  // === PHASES ===

  /**
   * GET /projects/:projectId/phases - Liste des phases
   */
  getPhases: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/phases?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/:projectId/phases - Créer une phase
   */
  createPhase: async (projectId: string, data: {
    companyId: string;
    code: string;
    name: string;
    description?: string;
    position?: number;
    startDate?: string;
    endDate?: string;
    budgetHT?: number;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/phases`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * PUT /projects/:projectId/phases/:phaseId - Mettre à jour une phase
   */
  updatePhase: async (projectId: string, phaseId: string, data: {
    companyId: string;
    code?: string;
    name?: string;
    description?: string;
    position?: number;
    startDate?: string;
    endDate?: string;
    budgetHT?: number;
    progressPercent?: number;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/phases/${phaseId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * DELETE /projects/:projectId/phases/:phaseId - Supprimer une phase
   */
  deletePhase: async (projectId: string, phaseId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/phases/${phaseId}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return true;
  },

  // === PHASE DEPENDENCIES ===

  /**
   * GET /projects/:projectId/phases/dependencies - Dépendances entre phases
   */
  getPhaseDependencies: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/phases/dependencies?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/:projectId/phases/dependencies - Créer une dépendance
   */
  createPhaseDependency: async (projectId: string, data: {
    companyId: string;
    predecessorId: string;
    successorId: string;
    dependencyType: string;
    lagDays: number;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/phases/dependencies`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * DELETE /projects/:projectId/phases/dependencies/:dependencyId - Supprimer une dépendance
   */
  deletePhaseDependency: async (projectId: string, dependencyId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/phases/dependencies/${dependencyId}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return true;
  },

  /**
   * PATCH /projects/:projectId/phases/:phaseId/reschedule - Replanifier une phase
   */
  reschedulePhase: async (projectId: string, phaseId: string, data: {
    companyId: string;
    startDate: string;
    endDate: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/phases/${phaseId}/reschedule`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/:projectId/phases/critical-path - Chemin critique
   */
  getCriticalPath: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/phases/critical-path?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  // === COST PLANS ERP ===

  /**
   * GET /projects/:projectId/cost-plans - Liste des plans de coûts
   */
  getCostPlans: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/cost-plans?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * PUT /projects/:projectId/cost-plans/:costPlanId - Mettre à jour un plan de coût
   */
  updateCostPlan: async (projectId: string, costPlanId: string, data: {
    companyId: string;
    phaseId?: string;
    costType?: string;
    description?: string;
    unit?: string;
    quantity?: number;
    unitPrice?: number;
    plannedAmountHt?: number;
    supplierId?: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/cost-plans/${costPlanId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * DELETE /projects/:projectId/cost-plans/:costPlanId - Supprimer un plan de coût
   */
  deleteCostPlan: async (projectId: string, costPlanId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/cost-plans/${costPlanId}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return true;
  },

  // === ACTUAL COSTS ===

  /**
   * GET /projects/:projectId/actual-costs - Liste des coûts réels
   */
  getActualCosts: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/actual-costs?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * PUT /projects/:projectId/actual-costs/:costId - Mettre à jour un coût réel
   */
  updateActualCost: async (projectId: string, costId: string, data: {
    companyId: string;
    phaseId?: string;
    costType?: string;
    description?: string;
    amountHt?: number;
    amountTtc?: number;
    vatRate?: number;
    dateIncurred?: string;
    status?: string;
    supplierId?: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/actual-costs/${costId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * DELETE /projects/:projectId/actual-costs/:costId - Supprimer un coût réel
   */
  deleteActualCost: async (projectId: string, costId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/actual-costs/${costId}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return true;
  },

  // === PROGRESS INVOICES (Situations de travaux) - ERP BTP ===
  // Workflow: DRAFT → SUBMITTED → VALIDATED → INVOICED → PAID

  /**
   * GET /projects/situations - Toutes les situations de tous les projets
   */
  getAllProgressInvoices: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/situations?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return {
      situations: apiResponse.data || [],
      stats: apiResponse.stats || {
        totalSituations: 0,
        totalInvoiced: 0,
        averageProgress: 0,
        pendingSituations: 0,
        paidSituations: 0,
      },
    };
  },

  /**
   * GET /projects/:projectId/situations - Liste des situations d'un projet
   */
  getProgressInvoices: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/situations?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/:projectId/situations/:situationId - Détail d'une situation par ID
   */
  getProgressInvoiceById: async (projectId: string, situationId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/situations/${situationId}?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/:projectId/situations/preview - Prévisualiser la prochaine situation
   */
  getProgressInvoicePreview: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/situations/preview?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/:projectId/situations/summary - Résumé des situations
   */
  getProgressInvoicesSummary: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/situations/summary?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/:projectId/situations - Créer une situation
   * Nouveau format avec lignes détaillées et options de retenue
   */
  createProgressInvoice: async (projectId: string, data: {
    companyId: string;
    periodStart: string;
    periodEnd: string;
    retentionType?: 'NONE' | 'GUARANTEE' | 'BONNE_FIN' | 'PRORATA' | 'CUSTOM';
    retentionPercent?: number;
    retentionReleaseDate?: string;
    priceRevisionEnabled?: boolean;
    baseIndexType?: string;
    baseIndexValue?: number;
    currentIndexValue?: number;
    internalNotes?: string;
    clientNotes?: string;
    lines: {
      phaseId?: string;
      description: string;
      unit?: string;
      quantity?: number;
      unitPriceHT?: number;
      contractAmountHT: number;
      previousPercent: number;
      currentPercent: number;
      vatRate?: number;
    }[];
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/situations`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      let errorMessage = 'Erreur lors de la création de la situation';
      if (typeof errorData?.error === 'string') {
        errorMessage = errorData.error;
      } else if (typeof errorData?.error?.message === 'string') {
        errorMessage = errorData.error.message;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.errors?.[0]?.message) {
        errorMessage = errorData.errors[0].message;
      }

      throw new Error(errorMessage);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * PUT /projects/:projectId/situations/:situationId - Mettre à jour une situation (DRAFT seulement)
   */
  updateProgressInvoice: async (projectId: string, situationId: string, data: {
    companyId: string;
    periodStart?: string;
    periodEnd?: string;
    retentionType?: 'NONE' | 'GUARANTEE' | 'BONNE_FIN' | 'PRORATA' | 'CUSTOM';
    retentionPercent?: number;
    retentionReleaseDate?: string;
    priceRevisionEnabled?: boolean;
    baseIndexType?: string;
    baseIndexValue?: number;
    currentIndexValue?: number;
    internalNotes?: string;
    clientNotes?: string;
    lines?: {
      id?: string;
      phaseId?: string;
      description?: string;
      unit?: string;
      quantity?: number;
      unitPriceHT?: number;
      contractAmountHT?: number;
      currentPercent?: number;
      vatRate?: number;
    }[];
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/situations/${situationId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * DELETE /projects/:projectId/situations/:situationId - Supprimer une situation (DRAFT seulement)
   */
  deleteProgressInvoice: async (projectId: string, situationId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/situations/${situationId}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return { success: true };
  },

  // --- Workflow Actions ---

  /**
   * POST /projects/:projectId/situations/:situationId/submit - Soumettre pour validation
   */
  submitProgressInvoice: async (projectId: string, situationId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/situations/${situationId}/submit`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/:projectId/situations/:situationId/validate - Valider une situation
   */
  validateProgressInvoice: async (projectId: string, situationId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/situations/${situationId}/validate`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/:projectId/situations/:situationId/reject - Rejeter une situation
   */
  rejectProgressInvoice: async (projectId: string, situationId: string, companyId: string, reason: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/situations/${situationId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ companyId, reason }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/:projectId/situations/:situationId/generate-invoice - Générer la facture
   */
  generateInvoiceFromSituation: async (projectId: string, situationId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/situations/${situationId}/generate-invoice`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  // --- Retentions ---

  /**
   * GET /projects/retentions/to-release - Retenues à libérer
   */
  getRetentionsToRelease: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/retentions/to-release?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/:projectId/situations/:situationId/retentions - Retenues d'une situation
   */
  getRetentionsBySituation: async (projectId: string, situationId: string, companyId: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/projects/${projectId}/situations/${situationId}/retentions?companyId=${companyId}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/:projectId/situations/:situationId/release - Libérer une retenue
   */
  releaseRetention: async (
    projectId: string,
    situationId: string,
    data: {
      companyId: string;
      releaseType?: string;
      releaseDate?: string;
      releaseAmount: number;
      notes?: string;
    }
  ) => {
    const response = await fetchWithAuth(
      `${API_URL}/projects/${projectId}/situations/${situationId}/release`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/retentions/:retentionId/invoice - Facturer une libération de retenue
   */
  invoiceRetentionRelease: async (retentionId: string, companyId: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/projects/retentions/${retentionId}/invoice`,
      {
        method: 'POST',
        body: JSON.stringify({ companyId }),
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/:projectId/dgd - Décompte Général Définitif
   */
  getDGD: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/projects/${projectId}/dgd?companyId=${companyId}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/:projectId/final-invoice - Générer une facture de solde (FINAL)
   */
  generateFinalInvoice: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/projects/${projectId}/final-invoice?companyId=${companyId}`,
      {
        method: 'POST',
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/:projectId/situations/:situationId/export - Exporter données situation (PDF)
   */
  exportSituationData: async (projectId: string, situationId: string, companyId: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/projects/${projectId}/situations/${situationId}/export?companyId=${companyId}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/:projectId/situations/:situationId/pdf - Télécharger le PDF d'une situation
   */
  downloadSituationPDF: async (projectId: string, situationId: string, companyId: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/projects/${projectId}/situations/${situationId}/pdf?companyId=${companyId}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `situation-${situationId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  // === MATERIALS & EQUIPMENT ===

  /**
   * GET /projects/materials-equipment - Tous les matériaux et équipements
   */
  getMaterialsAndEquipment: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/materials-equipment?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || {
      planned: { materials: 0, equipment: 0, total: 0 },
      actual: { materials: 0, equipment: 0, total: 0 },
      variance: { materials: 0, equipment: 0, total: 0 },
      items: [],
    };
  },

  // === SUBCONTRACTING ===

  /**
   * GET /projects/subcontracting - Toutes les données de sous-traitance
   */
  getSubcontracting: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/subcontracting?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || {
      planned: { subcontracting: 0, total: 0 },
      actual: { subcontracting: 0, total: 0 },
      variance: { subcontracting: 0, total: 0 },
      items: [],
    };
  },

  // === DOCUMENTS (GED) ===

  /**
   * GET /projects/documents - Tous les documents de tous les projets
   */
  getAllDocuments: async (companyId: string, filter?: { documentType?: string }) => {
    const params = new URLSearchParams({ companyId });
    if (filter?.documentType) params.append('documentType', filter.documentType);

    const response = await fetchWithAuth(`${API_URL}/projects/documents?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return {
      documents: apiResponse.data || [],
      stats: apiResponse.stats || {
        totalDocuments: 0,
        totalSize: 0,
        byType: [],
        byProject: [],
      },
    };
  },

  /**
   * GET /projects/:projectId/documents - Liste des documents
   */
  getDocuments: async (projectId: string, companyId: string, filter?: { documentType?: string }) => {
    const params = new URLSearchParams({ companyId });
    if (filter?.documentType) params.append('documentType', filter.documentType);

    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/documents?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/:projectId/documents/stats - Statistiques documents
   */
  getDocumentStats: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/documents/stats?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/:projectId/documents - Upload document
   */
  uploadDocument: async (projectId: string, data: {
    companyId: string;
    documentType: string;
    fileName: string;
    fileUrl: string;
    fileSize: number;
    mimeType: string;
    description?: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * DELETE /projects/:projectId/documents/:docId - Supprimer document
   */
  deleteDocument: async (projectId: string, documentId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/documents/${documentId}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return true;
  },

  // === CASH FORECAST ===

  /**
   * GET /projects/:projectId/cash-forecast - Prévisions trésorerie
   */
  getCashForecast: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/cash-forecast?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  // === MARGIN ANALYSIS ===

  /**
   * GET /projects/:projectId/margin - Analyse de marge complète
   */
  getMarginAnalysis: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/margin?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/:projectId/variance - Rapport de variance
   */
  getVarianceReport: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/variance?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  // === PORTFOLIO ===

  /**
   * GET /projects/portfolio/dashboard - Tableau de bord portfolio
   * Maps backend DTO to frontend expected format
   */
  getPortfolioDashboard: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/portfolio/dashboard?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    const data = apiResponse.data || apiResponse;

    // Map backend DTO to frontend expected format
    return {
      totalProjects: data.summary?.totalProjects ?? 0,
      activeProjects: data.summary?.activeProjects ?? 0,
      totalBudget: data.summary?.totalBudgetHT ?? 0,
      totalCost: data.summary?.totalCostActual ?? 0,
      averageMargin: data.summary?.overallMarginPercent ?? 0,
      projectsAtRisk: data.atRiskProjects?.length ?? 0,
      topPerformers: (data.topMarginProjects || []).map((p: any) => ({
        projectId: p.projectId,
        name: p.projectName,
        marginPercent: p.marginPercent ?? 0,
      })),
      underperformers: (data.atRiskProjects || []).map((p: any) => ({
        projectId: p.projectId,
        name: p.projectName,
        marginPercent: p.marginPercent ?? 0,
      })),
      marginAlerts: (data.atRiskProjects || []).filter((p: any) => p.marginPercent < 0).map((p: any) => ({
        projectId: p.projectId,
        name: p.projectName,
        message: `Marge négative: ${p.marginPercent?.toFixed(1) ?? 0}%`,
        severity: p.marginPercent < -10 ? 'critical' : 'warning' as const,
      })),
    };
  },

  /**
   * POST /projects/:projectId/recalculate - Recalculer les totaux
   */
  recalculateTotals: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/recalculate`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  // ==================== SCHEDULE OPTIMIZER (ML) - Sprint 3.1 US-3.1.3 ====================

  /**
   * POST /projects/schedule/optimize - Optimiser le planning d'une journée
   */
  optimizeSchedule: async (companyId: string, data: {
    date: string;
    objective?: 'minimize_travel' | 'balance_workload' | 'maximize_skills' | 'respect_deadlines' | 'combined';
    teams?: any[];
    interventions?: any[];
    constraints?: Record<string, unknown>;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/schedule/optimize`, {
      method: 'POST',
      body: JSON.stringify({ companyId, ...data }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/schedule/teams - Équipes disponibles pour une date
   */
  getScheduleTeams: async (companyId: string, date: string) => {
    const params = new URLSearchParams({ companyId, date });
    const response = await fetchWithAuth(`${API_URL}/projects/schedule/teams?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /projects/schedule/work-orders - WorkOrders à planifier
   */
  getScheduleWorkOrders: async (companyId: string, date: string) => {
    const params = new URLSearchParams({ companyId, date });
    const response = await fetchWithAuth(`${API_URL}/projects/schedule/work-orders?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /projects/schedule/apply - Appliquer les suggestions d'optimisation
   */
  applyScheduleOptimization: async (companyId: string, suggestions: any[]) => {
    const response = await fetchWithAuth(`${API_URL}/projects/schedule/apply`, {
      method: 'POST',
      body: JSON.stringify({ companyId, suggestions }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },
};

// ============================================
// ⏱️ TIMESHEETS API (CRM Artisans)
// ============================================
export const timesheetsAPI = {
  /**
   * GET /timesheets - Liste avec filtres
   */
  getAll: async (companyId: string, filters?: {
    userId?: string;
    projectId?: string;
    workOrderId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) => {
    const params = new URLSearchParams({ companyId });
    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.projectId) params.append('projectId', filters.projectId);
    if (filters?.workOrderId) params.append('workOrderId', filters.workOrderId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await fetchWithAuth(`${API_URL}/projects/timesheets?${params}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération des feuilles de temps');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * GET /timesheets/mine - Mes feuilles de temps
   */
  getMine: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/mine?companyId=${companyId}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération de mes feuilles de temps');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * GET /timesheets/by-work-order - Par intervention (CRM Artisans)
   */
  getByWorkOrder: async (workOrderId: string, companyId: string) => {
    const params = new URLSearchParams({ workOrderId, companyId });
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/by-work-order?${params}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération des feuilles de temps');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * GET /timesheets/pending-approvals - En attente d'approbation
   */
  getPendingApprovals: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/pending-approvals?companyId=${companyId}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération des feuilles en attente');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * GET /timesheets/stats - Statistiques
   */
  getStats: async (companyId: string, period?: { start: string; end: string }) => {
    const params = new URLSearchParams({ companyId });
    if (period?.start) params.append('periodStart', period.start);
    if (period?.end) params.append('periodEnd', period.end);

    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/stats?${params}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération des statistiques');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * GET /timesheets/:id - Récupérer par ID
   */
  getById: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/${id}?companyId=${companyId}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Feuille de temps non trouvée');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * POST /timesheets - Créer une feuille de temps
   */
  create: async (data: {
    companyId: string;
    projectId?: string;
    workOrderId?: string;
    date: string;
    periodType?: 'DAILY' | 'WEEKLY';
    notes?: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la création de la feuille de temps');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * PUT /timesheets/:id - Mettre à jour
   */
  update: async (id: string, data: {
    companyId: string;
    projectId?: string;
    workOrderId?: string;
    date?: string;
    periodType?: 'DAILY' | 'WEEKLY';
    notes?: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la mise à jour');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * DELETE /timesheets/:id - Supprimer
   */
  delete: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/${id}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la suppression');
    }

    return { success: true };
  },

  // ==================== ENTRIES ====================

  /**
   * POST /timesheets/:id/entries - Ajouter une entrée
   */
  addEntry: async (timesheetId: string, data: {
    companyId: string;
    date: string;
    startTime?: string;
    endTime?: string;
    breakMinutes?: number;
    hours: number;
    hourlyRate?: number;
    description?: string;
    taskType?: string;
    isBillable?: boolean;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/${timesheetId}/entries`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de l\'ajout de l\'entrée');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * PUT /timesheets/:id/entries/:entryId - Mettre à jour une entrée
   */
  updateEntry: async (timesheetId: string, entryId: string, data: {
    companyId: string;
    date?: string;
    startTime?: string;
    endTime?: string;
    breakMinutes?: number;
    hours?: number;
    hourlyRate?: number;
    description?: string;
    taskType?: string;
    isBillable?: boolean;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/${timesheetId}/entries/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la mise à jour de l\'entrée');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * DELETE /timesheets/:id/entries/:entryId - Supprimer une entrée
   */
  deleteEntry: async (timesheetId: string, entryId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/${timesheetId}/entries/${entryId}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la suppression de l\'entrée');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  // ==================== WORKFLOW ====================

  /**
   * POST /timesheets/:id/submit - Soumettre pour approbation
   */
  submit: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/${id}/submit`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la soumission');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * POST /timesheets/:id/approve - Approuver
   */
  approve: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de l\'approbation');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * POST /timesheets/:id/reject - Rejeter
   */
  reject: async (id: string, companyId: string, reason: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/timesheets/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ companyId, reason }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors du rejet');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },
};

// ============================================
// 👥 PROJECT MEMBERS API (ERP BTP - Équipes Chantier)
// ============================================
export const projectMembersAPI = {
  /**
   * GET /projects/:projectId/members - Liste des membres d'un projet
   */
  getProjectMembers: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/members?companyId=${companyId}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération des membres');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * GET /projects/:projectId/members/available - Utilisateurs disponibles (non membres)
   */
  getAvailableUsers: async (projectId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/members/available?companyId=${companyId}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération des utilisateurs disponibles');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * POST /projects/:projectId/members - Ajouter un membre
   */
  addMember: async (projectId: string, data: {
    companyId: string;
    userId: string;
    role?: 'MANAGER' | 'MEMBER' | 'TECHNICIAN' | 'VIEWER';
    hourlyRate?: number;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de l\'ajout du membre');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * POST /projects/:projectId/members/bulk - Ajouter plusieurs membres
   */
  addMembers: async (projectId: string, data: {
    companyId: string;
    userIds: string[];
    role?: 'MANAGER' | 'MEMBER' | 'TECHNICIAN' | 'VIEWER';
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/members/bulk`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de l\'ajout des membres');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * PUT /projects/:projectId/members/:userId - Mettre à jour un membre
   */
  updateMember: async (projectId: string, userId: string, data: {
    companyId: string;
    role?: 'MANAGER' | 'MEMBER' | 'TECHNICIAN' | 'VIEWER';
    hourlyRate?: number | null;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/members/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la mise à jour du membre');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * DELETE /projects/:projectId/members/:userId - Retirer un membre
   */
  removeMember: async (projectId: string, userId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/projects/${projectId}/members/${userId}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors du retrait du membre');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },
};

// ============================================
// 🔧 WORK ORDERS API (CRM Artisans)
// ============================================
export const workOrdersAPI = {
  /**
   * GET /work-orders - Liste des bons de travail
   */
  getAll: async (companyId: string, options?: {
    page?: number;
    limit?: number;
    status?: string;
    assignedToUserId?: string;
  }) => {
    const params = new URLSearchParams({ companyId });
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.status) params.append('status', options.status);
    if (options?.assignedToUserId) params.append('assignedToUserId', options.assignedToUserId);

    const response = await fetchWithAuth(`${API_URL}/work-orders?${params}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération des bons de travail');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * GET /work-orders/stats - Statistiques
   */
  getStats: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/work-orders/stats?companyId=${companyId}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération des statistiques');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * GET /team-members - Liste des membres de l'équipe
   */
  getTeamMembers: async (companyId: string): Promise<Array<{ id: string; fullName: string; email: string; role: string }>> => {
    const response = await fetchWithAuth(`${API_URL}/work-orders/team-members?companyId=${companyId}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération des membres');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * GET /work-orders/technician-schedule - Planning technicien
   */
  getTechnicianSchedule: async (companyId: string, userId: string, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams({ companyId, userId });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await fetchWithAuth(`${API_URL}/work-orders/technician-schedule?${params}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération du planning');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * GET /work-orders/:id - Détails d'un bon de travail
   */
  getById: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/work-orders/${id}?companyId=${companyId}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération du bon de travail');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * POST /work-orders - Créer un bon de travail
   */
  create: async (data: {
    companyId: string;
    clientId: string;
    title: string;
    problemDescription?: string;
    workNotesInternal?: string;
    workNotesPublic?: string;
    serviceLocationAddress1?: string;
    serviceLocationAddress2?: string;
    serviceLocationZipCode?: string;
    serviceLocationCity?: string;
    estimatedDuration?: number;
    generatedFromQuoteId?: string;
    assignedToUserId?: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/work-orders`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la création du bon de travail');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * PUT /work-orders/:id - Mettre à jour un bon de travail
   */
  update: async (id: string, data: {
    companyId: string;
    title?: string;
    problemDescription?: string;
    workNotesInternal?: string;
    workNotesPublic?: string;
    serviceLocationAddress1?: string;
    serviceLocationAddress2?: string;
    serviceLocationZipCode?: string;
    serviceLocationCity?: string;
    estimatedDuration?: number;
    beforePhotosUrls?: string[];
    afterPhotosUrls?: string[];
  }) => {
    const response = await fetchWithAuth(`${API_URL}/work-orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la mise à jour du bon de travail');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * DELETE /work-orders/:id - Supprimer un bon de travail
   */
  delete: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/work-orders/${id}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la suppression du bon de travail');
    }

    return { success: true };
  },

  /**
   * PATCH /work-orders/:id/status - Changer le statut
   */
  updateStatus: async (id: string, companyId: string, status: string) => {
    const response = await fetchWithAuth(`${API_URL}/work-orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ companyId, status }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors du changement de statut');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * PATCH /work-orders/:id/assign - Assigner un technicien
   */
  assignTechnician: async (id: string, companyId: string, userId: string) => {
    const response = await fetchWithAuth(`${API_URL}/work-orders/${id}/assign`, {
      method: 'PATCH',
      body: JSON.stringify({ companyId, userId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de l\'assignation du technicien');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * POST /work-orders/:id/schedule-events - Ajouter un événement de planning
   */
  addScheduleEvent: async (workOrderId: string, data: {
    companyId: string;
    technicianUserId?: string;
    startAt: string;
    endAt: string;
    notes?: string;
  }) => {
    const response = await fetchWithAuth(`${API_URL}/work-orders/${workOrderId}/schedule-events`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de l\'ajout de l\'événement');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * POST /work-orders/:id/link-invoice - Lier à une facture
   */
  linkInvoice: async (id: string, companyId: string, invoiceId: string) => {
    const response = await fetchWithAuth(`${API_URL}/work-orders/${id}/link-invoice`, {
      method: 'POST',
      body: JSON.stringify({ companyId, invoiceId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la liaison à la facture');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * POST /work-orders/:id/photos - Upload une photo avant/après
   * 🔒 Stockage isolé: tenant/company/année/mois/workOrderId/type
   */
  uploadPhoto: async (
    workOrderId: string,
    companyId: string,
    photo: File,
    photoType: 'before' | 'after',
    options?: {
      capturedAt?: Date;
      latitude?: number;
      longitude?: number;
      deviceInfo?: string;
    }
  ) => {
    const formData = new FormData();
    formData.append('photo', photo);
    formData.append('companyId', companyId);
    formData.append('photoType', photoType);
    if (options?.capturedAt) formData.append('capturedAt', options.capturedAt.toISOString());
    if (options?.latitude) formData.append('latitude', options.latitude.toString());
    if (options?.longitude) formData.append('longitude', options.longitude.toString());
    if (options?.deviceInfo) formData.append('deviceInfo', options.deviceInfo);

    const response = await fetchWithAuth(`${API_URL}/work-orders/${workOrderId}/photos`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de l\'upload de la photo');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },

  /**
   * GET /work-orders/:id/photos - Récupérer les URLs signées des photos
   */
  getPhotos: async (workOrderId: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/work-orders/${workOrderId}/photos?companyId=${companyId}`);


    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la récupération des photos');
    }

    const json = await safeJsonParse(response);
    return json.data || { before: [], after: [] };
  },

  /**
   * DELETE /work-orders/:id/photos/:photoKey - Supprimer une photo
   */
  deletePhoto: async (workOrderId: string, companyId: string, photoKey: string) => {
    const encodedKey = encodeURIComponent(photoKey);
    const response = await fetchWithAuth(
      `${API_URL}/work-orders/${workOrderId}/photos/${encodedKey}?companyId=${companyId}`,
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la suppression de la photo');
    }

    return { success: true };
  },

  /**
   * GET /work-orders/:id/report - Telecharger le rapport PDF d'intervention
   * Retourne un Blob PDF
   */
  downloadReport: async (workOrderId: string, companyId: string): Promise<Blob> => {
    const response = await fetchWithAuth(
      `${API_URL}/work-orders/${workOrderId}/report?companyId=${companyId}`,
      {
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la generation du rapport');
    }

    return response.blob();
  },

  /**
   * POST /work-orders/:id/generate-invoice - Générer une facture depuis un WO terminé
   */
  generateInvoice: async (id: string) => {
    const response = await fetchWithAuth(`${API_URL}/work-orders/${id}/generate-invoice`, {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error?.message || 'Erreur lors de la génération de la facture');
    }

    const json = await safeJsonParse(response);
    return json.data || json;
  },
};

// ============================================
// 🎯 CRM API (Pipeline Commercial)
// ============================================

export const leadsAPI = {
  /**
   * GET /crm/leads - Liste des leads
   */
  getAll: async (companyId: string, options?: {
    page?: number;
    limit?: number;
    status?: string;
    source?: string;
    assignedToUserId?: string;
    temperature?: string;
    search?: string;
  }) => {
    const params = new URLSearchParams({ companyId });
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.status) params.append('status', options.status);
    if (options?.source) params.append('source', options.source);
    if (options?.assignedToUserId) params.append('assignedToUserId', options.assignedToUserId);
    if (options?.temperature) params.append('temperature', options.temperature);
    if (options?.search) params.append('search', options.search);

    const response = await fetchWithAuth(`${API_URL}/crm/leads?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return {
      data: apiResponse.data || [],
      meta: apiResponse.meta || { page: 1, limit: 20, total: 0, pages: 0 },
    };
  },

  /**
   * GET /crm/leads/search - Recherche de leads
   */
  search: async (companyId: string, query: string) => {
    const params = new URLSearchParams({ companyId, q: query });

    const response = await fetchWithAuth(`${API_URL}/crm/leads/search?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/leads/stats - Statistiques leads
   */
  getStats: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/stats?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/leads/:id - Détails d'un lead
   */
  getById: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/${id}?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/leads - Créer un lead
   */
  create: async (data: Record<string, unknown>) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * PUT /crm/leads/:id - Mettre à jour un lead
   */
  update: async (id: string, data: Record<string, unknown>) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * DELETE /crm/leads/:id - Supprimer un lead
   */
  delete: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/${id}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return { success: true };
  },

  /**
   * PATCH /crm/leads/:id/status - Changer le statut
   */
  updateStatus: async (id: string, companyId: string, status: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ companyId, status }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/leads/:id/qualify - Qualifier un lead
   */
  qualify: async (id: string, companyId: string, score: number, notes?: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/${id}/qualify`, {
      method: 'POST',
      body: JSON.stringify({ companyId, score, notes }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/leads/:id/assign - Assigner un lead
   */
  assign: async (id: string, companyId: string, userId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ companyId, userId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/leads/:id/convert-to-client - Convertir en client
   */
  convertToClient: async (id: string, data: Record<string, unknown>) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/${id}/convert-to-client`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/leads/:id/convert-to-opportunity - Convertir en opportunité
   */
  convertToOpportunity: async (id: string, data: Record<string, unknown>) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/${id}/convert-to-opportunity`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/leads/:id/activities - Récupérer les activités
   */
  getActivities: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/${id}/activities?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/leads/:id/activities - Enregistrer une activité
   */
  logActivity: async (id: string, data: { companyId: string; activityType: string; description?: string; metadata?: Record<string, unknown> }) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/${id}/activities`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  // ==================== LEAD SCORING (ML) - Sprint 3.1 US-3.1.2 ====================

  /**
   * POST /crm/leads/:id/score - Score un lead via ML
   */
  scoreLead: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/${id}/score?companyId=${companyId}`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/leads/score-batch - Score plusieurs leads en batch
   */
  scoreLeadsBatch: async (companyId: string, leadIds?: string[]) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/score-batch`, {
      method: 'POST',
      body: JSON.stringify({ companyId, leadIds }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/leads/historical-data - Données historiques de conversion
   */
  getHistoricalData: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/historical-data?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  // ==================== LEAD IMPORT (CSV) ====================

  /**
   * GET /crm/leads/import/template - Télécharger template CSV
   */
  getImportTemplate: async (): Promise<Blob> => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/import/template`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return response.blob();
  },

  /**
   * POST /crm/leads/import/validate - Valider un fichier CSV avant import
   */
  validateImport: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);


    const response = await fetchWithAuth(`${API_URL}/crm/leads/import/validate`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/leads/import - Importer leads depuis CSV
   */
  importLeads: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);


    const response = await fetchWithAuth(`${API_URL}/crm/leads/import`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/leads/:id/convert-to-quote - Convertir en devis (1-click)
   */
  convertToQuote: async (id: string, data?: { title?: string }) => {
    const response = await fetchWithAuth(`${API_URL}/crm/leads/${id}/convert-to-quote`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },
};

export const opportunitiesAPI = {
  /**
   * GET /crm/opportunities - Liste des opportunités
   */
  getAll: async (companyId: string, options?: {
    page?: number;
    limit?: number;
    stage?: string;
    assignedToUserId?: string;
    clientId?: string;
    leadId?: string;
    search?: string;
    expectedCloseDateFrom?: string;
    expectedCloseDateTo?: string;
  }) => {
    const params = new URLSearchParams({ companyId });
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.stage) params.append('stage', options.stage);
    if (options?.assignedToUserId) params.append('assignedToUserId', options.assignedToUserId);
    if (options?.clientId) params.append('clientId', options.clientId);
    if (options?.leadId) params.append('leadId', options.leadId);
    if (options?.search) params.append('search', options.search);
    if (options?.expectedCloseDateFrom) params.append('expectedCloseDateFrom', options.expectedCloseDateFrom);
    if (options?.expectedCloseDateTo) params.append('expectedCloseDateTo', options.expectedCloseDateTo);

    const response = await fetchWithAuth(`${API_URL}/crm/opportunities?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return {
      data: apiResponse.data || [],
      meta: apiResponse.meta || { page: 1, limit: 20, total: 0, pages: 0 },
    };
  },

  /**
   * GET /crm/opportunities/pipeline-stats - Statistiques du pipeline
   */
  getPipelineStats: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/pipeline-stats?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/opportunities/forecast - Prévisions de vente
   */
  getForecast: async (companyId: string, months?: number) => {
    const params = new URLSearchParams({ companyId });
    if (months) params.append('months', months.toString());

    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/forecast?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/opportunities/:id - Détails d'une opportunité
   */
  getById: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/${id}?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/opportunities - Créer une opportunité
   */
  create: async (data: Record<string, unknown>) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * PUT /crm/opportunities/:id - Mettre à jour une opportunité
   */
  update: async (id: string, data: Record<string, unknown>) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * DELETE /crm/opportunities/:id - Supprimer une opportunité
   */
  delete: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/${id}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return { success: true };
  },

  /**
   * PATCH /crm/opportunities/:id/stage - Changer d'étape
   */
  moveToStage: async (id: string, companyId: string, stage: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/${id}/stage`, {
      method: 'PATCH',
      body: JSON.stringify({ companyId, stage }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/opportunities/:id/close-won - Clôturer comme gagné
   */
  closeAsWon: async (id: string, companyId: string, notes?: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/${id}/close-won`, {
      method: 'POST',
      body: JSON.stringify({ companyId, notes }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/opportunities/:id/close-lost - Clôturer comme perdu
   */
  closeAsLost: async (id: string, companyId: string, reason?: string, notes?: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/${id}/close-lost`, {
      method: 'POST',
      body: JSON.stringify({ companyId, reason, notes }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/opportunities/:id/reopen - Réouvrir
   */
  reopen: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/${id}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/opportunities/:id/generate-quote - Générer un devis
   */
  generateQuote: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/${id}/generate-quote`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/opportunities/:id/link-quote - Lier un devis existant
   */
  linkQuote: async (id: string, companyId: string, quoteId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/${id}/link-quote`, {
      method: 'POST',
      body: JSON.stringify({ companyId, quoteId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/opportunities/:id/activities - Récupérer les activités
   */
  getActivities: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/${id}/activities?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/opportunities/:id/activities - Enregistrer une activité
   */
  logActivity: async (id: string, data: { companyId: string; activityType: string; description?: string; metadata?: Record<string, unknown> }) => {
    const response = await fetchWithAuth(`${API_URL}/crm/opportunities/${id}/activities`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },
};

export const salesTasksAPI = {
  /**
   * GET /crm/sales-tasks - Liste des tâches
   */
  getAll: async (companyId: string, options?: {
    page?: number;
    limit?: number;
    taskType?: string;
    priority?: string;
    assignedToUserId?: string;
    leadId?: string;
    opportunityId?: string;
    clientId?: string;
    isCompleted?: boolean;
    dueDateFrom?: string;
    dueDateTo?: string;
  }) => {
    const params = new URLSearchParams({ companyId });
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.taskType) params.append('taskType', options.taskType);
    if (options?.priority) params.append('priority', options.priority);
    if (options?.assignedToUserId) params.append('assignedToUserId', options.assignedToUserId);
    if (options?.leadId) params.append('leadId', options.leadId);
    if (options?.opportunityId) params.append('opportunityId', options.opportunityId);
    if (options?.clientId) params.append('clientId', options.clientId);
    if (options?.isCompleted !== undefined) params.append('isCompleted', options.isCompleted.toString());
    if (options?.dueDateFrom) params.append('dueDateFrom', options.dueDateFrom);
    if (options?.dueDateTo) params.append('dueDateTo', options.dueDateTo);

    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return {
      data: apiResponse.data || [],
      meta: apiResponse.meta || { page: 1, limit: 20, total: 0, pages: 0 },
    };
  },

  /**
   * GET /crm/sales-tasks/mine - Mes tâches
   */
  getMyTasks: async (companyId: string, includeCompleted?: boolean) => {
    const params = new URLSearchParams({ companyId });
    if (includeCompleted) params.append('includeCompleted', 'true');

    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks/mine?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/sales-tasks/today - Tâches du jour
   */
  getTodayTasks: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks/today?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/sales-tasks/overdue - Tâches en retard
   */
  getOverdueTasks: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks/overdue?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/sales-tasks/stats - Statistiques tâches
   */
  getStats: async (companyId: string, userId?: string) => {
    const params = new URLSearchParams({ companyId });
    if (userId) params.append('userId', userId);

    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks/stats?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/sales-tasks/:id - Détails d'une tâche
   */
  getById: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks/${id}?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/sales-tasks - Créer une tâche
   */
  create: async (data: Record<string, unknown>) => {
    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * PUT /crm/sales-tasks/:id - Mettre à jour une tâche
   */
  update: async (id: string, data: Record<string, unknown>) => {
    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * DELETE /crm/sales-tasks/:id - Supprimer une tâche
   */
  delete: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks/${id}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return { success: true };
  },

  /**
   * POST /crm/sales-tasks/:id/complete - Compléter une tâche
   */
  complete: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/sales-tasks/:id/reschedule - Reprogrammer une tâche
   */
  reschedule: async (id: string, companyId: string, newDueDate: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks/${id}/reschedule`, {
      method: 'POST',
      body: JSON.stringify({ companyId, newDueDate }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/sales-tasks/:id/assign - Assigner une tâche
   */
  assign: async (id: string, companyId: string, userId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ companyId, userId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/sales-tasks/follow-up/lead - Créer un suivi pour un lead
   */
  createFollowUpForLead: async (companyId: string, leadId: string, dueDate: string, assignedToUserId?: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks/follow-up/lead`, {
      method: 'POST',
      body: JSON.stringify({ companyId, leadId, dueDate, assignedToUserId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/sales-tasks/follow-up/opportunity - Créer un suivi pour une opportunité
   */
  createFollowUpForOpportunity: async (companyId: string, opportunityId: string, dueDate: string, assignedToUserId?: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/sales-tasks/follow-up/opportunity`, {
      method: 'POST',
      body: JSON.stringify({ companyId, opportunityId, dueDate, assignedToUserId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },
};

// ============================================
// TREASURY API (Sprint 3.1 - US-3.1.5)
// ============================================

export const treasuryAPI = {
  /**
   * GET /api/v1/treasury/projects/:projectId/forecast
   * Generate treasury forecast for a specific project (chantier)
   * Sprint 3.1 - US-3.1.5: Prévision Trésorerie Chantier
   *
   * @param projectId - Project/Chantier ID
   * @param daysAhead - Forecast horizon in days (default: 90, max: 365)
   */
  getProjectForecast: async (projectId: string, daysAhead: number = 90) => {
    const response = await fetchWithAuth(
      `${API_URL}/treasury/projects/${projectId}/forecast?daysAhead=${daysAhead}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },
};

// ============================================
// CRM AUTOMATION RULES API
// ============================================

export const automationAPI = {
  /**
   * GET /crm/automation/rules - Liste des règles d'automatisation
   */
  getRules: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/automation/rules?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/automation/rules/:id - Détails d'une règle
   */
  getRuleById: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/automation/rules/${id}?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * POST /crm/automation/rules - Créer une règle
   */
  createRule: async (data: Record<string, any>) => {
    const response = await fetchWithAuth(`${API_URL}/crm/automation/rules`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * PUT /crm/automation/rules/:id - Mettre à jour une règle
   */
  updateRule: async (id: string, data: Record<string, any>) => {
    const response = await fetchWithAuth(`${API_URL}/crm/automation/rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * DELETE /crm/automation/rules/:id - Supprimer une règle
   */
  deleteRule: async (id: string, companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/automation/rules/${id}?companyId=${companyId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * PATCH /crm/automation/rules/:id/toggle - Activer/Désactiver une règle
   */
  toggleRule: async (id: string, companyId: string, isActive: boolean) => {
    const response = await fetchWithAuth(`${API_URL}/crm/automation/rules/${id}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({ companyId, isActive }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * GET /crm/automation/rules/:id/history - Historique d'exécution d'une règle
   */
  getExecutionHistory: async (ruleId: string, companyId: string, page: number = 1, limit: number = 20) => {
    const params = new URLSearchParams({
      companyId,
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await fetchWithAuth(`${API_URL}/crm/automation/rules/${ruleId}/history?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * POST /crm/automation/seed-templates - Créer les templates par profil métier
   */
  seedTemplates: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/automation/seed-templates`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    return safeJsonParse(response);
  },

  /**
   * GET /crm/automation/profile - Récupérer le profil métier de la company
   */
  getProfile: async (companyId: string) => {
    const response = await fetchWithAuth(`${API_URL}/crm/automation/profile?companyId=${companyId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(parseApiError(error));
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },
};

// ============================================
// RE-EXPORTS FROM SEPARATE API FILES
// ============================================
export { legalMentionsAPI } from './legalMentionsAPI';
export { clientsAPI } from './clientsAPI';
export { productsAPI } from './productsAPI';
export { paymentTermsAPI } from './paymentTermsAPI';

// Alias: companyAPI → companiesAPI (for backward compatibility)
// companiesAPI is defined above in this file
export { companiesAPI as companyAPI };

// Alias: adminAPI → api (generic admin operations use the base api object)
export const adminAPI = api;

export default api;
